import { parseGenerationPlanReceipt } from '@/lib/api/quality-generation-preparation.js';
import type { ApiRequestAuthority } from '@/lib/api-request-authority.js';
import type {
  BuiltinConnectorId,
  ConnectorMeta,
  ConnectorPermissionLevel,
  ConnectorStatusMap,
} from '@research-assistant/shared';
import { supportsConnectorWrite } from '@research-assistant/shared';
import { z } from 'zod';

import { browserTz } from '../../features/automations/tz';
import { useUiStore } from '../../features/settings/ui-store.js';
import { ApiError, apiErrorFromResponse } from '../../lib/api-error.js';
import { notify } from '../../lib/notify.js';
import { getRuntimeConfig } from '../../lib/runtime-config.js';
import { AGENT_TURN_MIN_VALIDITY_MS } from '../../lib/agent-turn-token.js';
import { ensureFreshPlatformJwt } from '../../lib/sso.js';
import {
  fileReferenceToWire,
  type FileReference,
} from '../../lib/api/file-chat.js';
import {
  parseDiagramLinks,
  parseDiagramSpec,
  parseDiagramSpecs,
} from '../../features/diagrams/diagram-spec.js';
import {
  dashboardSnapshotSchema,
  parseDashboardLinks,
} from '../../ui/dashboards/dashboard-snapshot';
import { setupActionFields } from '../setup-action.js';
import {
  effectivePendingInterrupt,
  parseInterruptGrant,
} from '../interrupt-card';
import {
  AutoApproval,
  Conversation,
  DeveloperConfirmation,
  DeveloperConfirmationDecision,
  InterruptDecision,
  Message,
  MessageRoute,
  OutgoingAttachment,
  ResumeDecision,
  ResumeDecisionType,
  ResumePayload,
  StreamEvent,
} from '../types';

const ConversationSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    // Timestamp of the last message — the stable "activity" key the sidebar orders and buckets by.
    // Unlike `updatedAt` it is not bumped by read-state / rename / pin. `.optional()` for the
    // rolling-deploy window; null for a conversation with no messages yet.
    lastMessageAt: z.string().nullable().optional(),
    pinnedAt: z.string().nullable(),
    projectId: z.string().nullable().optional(),
    // Registry workspace + per-conversation visibility (global-workspaces phase 2). `.optional()` for
    // the rolling-deploy window; visibility falls back to `private` in the type when absent.
    workspaceId: z.string().nullable().optional(),
    visibility: z.enum(['private', 'group', 'public']).optional(),
    // The group audience, as the single-conversation view returns it (`GET /v1/conversations/:id`).
    // Snake_case wire key pinned by the phase-4 contract (like a message's `sender_user_id`); without
    // this line zod strips it and the audience picker opens on an empty member set, so an untouched Save
    // wipes the group. Null on private/public and on list rows; absent from an older response.
    participant_ids: z.array(z.string()).nullable().optional().catch(null),
    // Server-computed unread flag. `.optional()` so old responses (during the
    // rolling-deploy window) parse without throwing.
    unread: z.boolean().optional(),
    status: z.enum(['active', 'archived']).catch('active').default('active'),
    archived_at: z.string().nullable().catch(null).default(null),
    can_manage: z.boolean().catch(false).default(false),
  })
  .transform(({ archived_at, can_manage, ...conversation }) => ({
    ...conversation,
    archivedAt: archived_at,
    canManage: can_manage,
  }));

const UploadedAttachmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  contentType: z.string(),
  mime: z.string().optional(),
  type: z.enum(['image', 'document', 'file']),
  size: z.number().optional(),
  url: z.string().optional(),
});

/**
 * The reference variant: a message that points at a workspace file instead of carrying uploaded bytes.
 * It has no `id` and no `type` — the absence of `id` is what tells the server this is a reference —
 * so the uploaded shape above can never parse it, and without this variant a persisted one would
 * fail the whole message page's parse.
 *
 * `revision` is OPTIONAL, and that is not a loosening. A file the agent delivered is pinned to the
 * revision the message carried; a file materialized from an external system is registered without one,
 * so the server sends none. A required field the server does not send fails the union, and an entry
 * that matches no member of the union is DROPPED — the chip would be absent with nothing red anywhere.
 *
 * `imported_at` and `web_url` are the two facts only a materialized file has: when its snapshot was
 * taken, and where the copy came from. They are declared because zod strips what it does not declare,
 * so without these lines the chip could never say either.
 */
const ArtifactAttachmentRefSchema = z.object({
  artifact_id: z.string(),
  revision: z.number().optional(),
  name: z.string(),
  content_type: z.string(),
  size: z.number(),
  url: z.string(),
  imported_at: z.string().optional(),
  web_url: z.string().optional(),
});

/**
 * The SharePoint variant: a message that points at a file in SharePoint. It has no `id`, no
 * `artifact_id` and no `type`, so neither shape above can parse it — and unparsable entries are dropped
 * by design, which means without this variant such a chip never reaches the history at all. Tried
 * first, matching the discrimination order the predicates use.
 *
 * `connection_id` is declared because zod STRIPS what it does not declare: without this line the
 * SharePoint the file lives in would survive the send and then vanish on the next load, and the chip
 * would be keyed as though the file were in the workspace's shared SharePoint.
 */
const SharePointAttachmentRefSchema = z.object({
  connection_id: z.string().optional(),
  site_id: z.string(),
  drive_id: z.string(),
  item_id: z.string(),
  name: z.string(),
  web_url: z.string().optional(),
});

/** Exact stored shape of a personal OneDrive live reference. Unknown coordinates are rejected. */
const OneDriveAttachmentRefSchema = z
  .object({
    source: z.literal('onedrive'),
    scope_id: z.string().trim().min(1),
    item_id: z.string().trim().min(1),
    name: z.string(),
    content_type: z.string().nullable(),
    size: z.number().nonnegative(),
    web_url: z.string().optional(),
  })
  .strict();

/**
 * A message's attachments, of any of the three shapes. Unparsable entries are dropped rather than
 * failing the message they belong to: a newer server can persist an attachment shape this build does
 * not know, and losing one attachment beats losing the whole conversation history (`fetchMessages`
 * parses a page at a time, so one bad row would blank everything).
 */
export const MessageAttachmentsSchema = z
  .array(
    z
      .union([
        OneDriveAttachmentRefSchema,
        SharePointAttachmentRefSchema,
        UploadedAttachmentSchema,
        ArtifactAttachmentRefSchema,
      ])
      .nullable()
      .catch(null),
  )
  .transform((entries) => entries.filter((entry) => entry !== null));

const MessageSchema = z.object({
  id: z.string(),
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  createdAt: z.string(),
  isStreaming: z.boolean().optional(),
  attachments: MessageAttachmentsSchema.nullable().optional(),
  authRequired: z
    .object({ provider: z.string(), lastMessage: z.string() })
    .optional(),
  feedbackRating: z.number().nullable().optional(),
  feedbackComment: z.string().nullable().optional(),
  status: z.enum(['streaming', 'completed', 'failed', 'cancelled']).optional(),
  errorReason: z.string().nullable().optional(),
  lastSeq: z.number().optional(),
  cancel_requested: z.boolean().optional().catch(undefined),
  turn_seq: z
    .number()
    .int()
    .min(0)
    .max(Number.MAX_SAFE_INTEGER)
    .optional()
    .catch(undefined),
  isWorkflowNotification: z.boolean().optional(),
  // Origin marker for synthetic automation-fired messages ('secretary' = an
  // out-of-band secretary post-back, rendered as its own turn) — zod strips
  // undeclared keys, so without this line the badge field never survives the
  // fetchMessages parse. `.catch(null)` keeps a NEWER server's unknown source
  // value from failing the whole conversation load (it degrades to null).
  source: z
    .enum([
      'automation',
      'automation_delivered',
      'automation_notice',
      'dashboard_refresh',
      'secretary',
      'quality_result',
      'skill_check',
      'developer',
    ])
    .nullable()
    .optional()
    .catch(null),
  // Pairing anchor (same zod-strip caveat as `source` above).
  anchorMessageId: z.string().nullable().optional(),
  /** Retry: a regenerated reply names its question; a question names the picked reply. */
  variantOf: z.string().nullable().optional().catch(null),
  activeVariantId: z.string().nullable().optional().catch(null),
  // Collaborative-chat speaker (global-workspaces phase 4). Snake_case wire keys pinned by the
  // phase-4 contract; without these lines zod strips them and the shared thread loses speaker labels.
  // `.catch(null)` keeps a newer/rolling-deploy backend from failing the whole `fetchMessages` parse.
  sender_user_id: z.string().nullable().optional().catch(null),
  sender_name: z.string().nullable().optional().catch(null),
  // Generative-dashboard snapshots persisted on the assistant message (same
  // zod-strip caveat) — an array, since one turn may produce several. Validated
  // as envelopes only; `doc` is opaque OpenUI Lang rendered by DashboardRenderer.
  dashboardSpecs: z.array(dashboardSnapshotSchema).nullable().optional(),
  /**
   * Which dashboard snapshots of this message were saved from chat, by spec index (same zod-strip
   * caveat). A malformed entry is dropped on its own; absent or null stays absent.
   */
  dashboardLinks: z
    .unknown()
    .transform((raw) => (raw == null ? undefined : parseDashboardLinks(raw))),
  /**
   * Diagram previews the agent emitted for this turn, and which of them were saved as diagrams (same
   * zod-strip caveat as `source`). A malformed entry is dropped on its own, and an absent or null
   * field stays absent, which the chat reads as none.
   */
  quality_generation_receipts: z
    .unknown()
    .transform((raw) =>
      Array.isArray(raw)
        ? raw
            .map(parseGenerationPlanReceipt)
            .filter(
              (receipt): receipt is NonNullable<typeof receipt> =>
                receipt !== null,
            )
        : undefined,
    ),
  diagramSpecs: z
    .unknown()
    .transform((raw) => (raw == null ? undefined : parseDiagramSpecs(raw))),
  diagramLinks: z
    .unknown()
    .transform((raw) => (raw == null ? undefined : parseDiagramLinks(raw))),
  // Insights run whose finished report renders inline in the assistant bubble
  // (same zod-strip caveat) — the poll is the sole carrier for an auto-mode fire.
  reportRunId: z.string().trim().min(1).nullable().optional().catch(null),
  /**
   * A generation run this turn is waiting for (camelCase on the wire, like reportRunId). The kind
   * says which card to mount; zod strips undeclared keys, so both must be named here.
   */
  workflowRunId: z.string().trim().min(1).nullable().optional().catch(null),
  workflowKind: z.string().trim().min(1).nullable().optional().catch(null),
  // Ordered insights-run progress steps (semantic keys + order). The poll carries
  // these so the streaming row shows English progress during an auto-mode fire.
  // `.catch(null)` keeps this additive field resilient: a malformed element
  // from a newer/rolling-deploy backend (e.g. `order` as a string) degrades the
  // steps to null instead of throwing the whole `fetchMessages` parse, so the
  // rest of the conversation still loads.
  steps: z
    .array(
      z.object({
        stageKey: z.string().trim().min(1),
        order: z.number().int().nonnegative(),
        at: z.string().optional().catch(undefined),
      }),
    )
    .nullable()
    .optional()
    .catch(null),
  /**
   * The reply's journaled agent steps, in order (same zod-strip caveat as `source`). The wire key
   * `name` holds the step text. `.catch(undefined)` turns a malformed list into "no steps", never a
   * failed parse of the conversation.
   */
  agentSteps: z
    .array(
      z
        .object({
          name: z.string().optional(),
          agent: z.string(),
          callId: z.string().optional(),
          operations: z.array(z.string()).optional(),
          operationOutcomes: z
            .array(
              z.object({
                name: z.string(),
                ok: z.boolean(),
                reason: z.string().optional(),
              }),
            )
            .optional(),
        })
        .transform(({ name, ...step }) =>
          name === undefined ? step : { text: name, ...step },
        ),
    )
    .optional()
    .catch(undefined),
  /**
   * ISO timestamp marking a resumed-but-silent HITL turn as resolved (same
   * zod-strip caveat as `source` above — without this line the field never
   * survives the fetchMessages parse). The SPA suppresses the stale approval
   * card + perpetual spinner on such a still-`streaming` message.
   */
  interruptResolvedAt: z.string().nullable().optional(),
  /**
   * Skill invocations batch-joined server-side (same zod-strip caveat as
   * `source` above). `.catch(null)` keeps this additive field resilient: a
   * malformed element from a newer/rolling-deploy backend degrades `skills`
   * to null instead of throwing the whole `fetchMessages` parse.
   */
  skills: z
    .array(z.object({ id: z.string(), title: z.string(), source: z.string() }))
    .nullable()
    .optional()
    .catch(null),
  /**
   * Persisted HITL interrupt card (the approval request the agent raised before
   * a write-capable tool call). Same zod-strip caveat as `source`. Lets the SPA
   * re-render the card from message-load state after a reload / once the broker
   * replay buffer's 5-min TTL has lapsed. `.catch(null)` keeps a newer server's
   * unknown shape from failing the whole conversation load.
   */
  interruptCard: z
    .object({
      interruptId: z.string(),
      agent: z.string().optional(),
      action: z
        .object({ tool: z.string(), args: z.record(z.unknown()).optional() })
        .optional(),
      description: z.string().optional(),
      allowedDecisions: z
        .array(z.enum(['approve', 'edit', 'reject']))
        .optional(),
      category: z.string().optional(),
      timestamp: z.string().optional(),
      /** What "Approve & allow similar" would allow; a malformed or absent grant reads as null. */
      grant: z.unknown().transform(parseInterruptGrant),
    })
    .nullable()
    .optional()
    .catch(null),
  /**
   * The user's decision on the persisted interrupt (approve / edit / reject),
   * set once the turn is resumed. Renders the resolved card ("requested X →
   * approved/edited/rejected"). An approve that also allowed similar operations
   * carries `remember_similar` or the `grant_id` it created; `auto_approved`
   * marks an operation a rule approved. Same resilience caveat as above.
   */
  interruptDecision: z
    .discriminatedUnion('type', [
      z.object({
        type: z.literal('approve'),
        remember_similar: z.boolean().optional(),
        grant_id: z.string().optional(),
      }),
      z.object({ type: z.literal('edit'), editedArgs: z.record(z.unknown()) }),
      z.object({ type: z.literal('reject'), feedback: z.string().optional() }),
      z.object({
        type: z.literal('auto_approved'),
        grant_id: z.string().optional(),
      }),
    ])
    .transform(toInterruptDecision)
    .nullable()
    .optional()
    .catch(null),
  /**
   * Names of the tools that failed during a turn that otherwise completed (same zod-strip caveat as
   * `source`). Present only when a tool failed. `.catch(undefined)` keeps a malformed value from
   * failing the whole `fetchMessages` parse; it degrades to no offer to report.
   */
  failedTools: z.array(z.string()).optional().catch(undefined),
  /**
   * Developer's question about an inline `#developer` and the sender's answer (same zod-strip caveat
   * as `source`). The message view keeps this key snake_case. `.catch(null)` turns a state a newer
   * server invents into "no question" instead of failing the whole `fetchMessages` parse.
   */
  developer_confirmation: z
    .enum(['pending', 'developer', 'agent'])
    .nullable()
    .optional()
    .catch(null),
  /**
   * The workspace, run and starter of a Developer message the chat follows for an `ask_developer`
   * handoff (MEDA-1194; same zod-strip caveat as `source`). A malformed value reads as none, so the
   * message renders as an ordinary Developer reply.
   */
  developer_watch: z
    .object({
      workspace_id: z.string(),
      workspace_name: z.string(),
      run_id: z.string().nullable(),
      started_by: z.string(),
      developer_conversation_id: z.string().nullable().optional().catch(null),
      awaiting_reply: z.boolean().optional().catch(false),
      reply_request: z
        .object({
          run_id: z.string(),
          questioned_at: z.string(),
          kind: z.enum(['question', 'plan']),
        })
        .optional()
        .catch(undefined),
      reply_receipt: z
        .object({
          answer: z.string(),
          action: z.enum(['answer', 'approve', 'request_changes']),
        })
        .optional()
        .catch(undefined),
      task: z.string().optional(),
    })
    .nullable()
    .optional()
    .catch(null),
  /**
   * The file change this turn made, kept for the viewer's Undo (same zod-strip caveat as `source`).
   * Only the keys the viewer reads are kept; a malformed record reads as "no edit".
   */
  file_edit: z
    .object({
      file: z.unknown(),
      undone_at: z.string().nullable().optional().default(null),
      undoable: z.boolean().optional().catch(undefined),
    })
    .nullable()
    .optional()
    .catch(null),
});

/** Map a stored decision from its wire keys to the client's {@link InterruptDecision}. */
function toInterruptDecision(
  stored:
    | { type: 'approve'; remember_similar?: boolean; grant_id?: string }
    | { type: 'edit'; editedArgs: Record<string, unknown> }
    | { type: 'reject'; feedback?: string }
    | { type: 'auto_approved'; grant_id?: string },
): InterruptDecision {
  if (stored.type === 'approve') {
    const similarAllowed =
      stored.remember_similar === true || stored.grant_id !== undefined;
    return similarAllowed
      ? { type: 'approve', similarAllowed: true }
      : { type: 'approve' };
  }
  if (stored.type === 'auto_approved') {
    return { type: 'auto_approved', grantId: stored.grant_id ?? null };
  }
  return stored;
}

function checkAccessDenied(res: Response): void {
  if (res.status === 403 && window.location.pathname !== '/access-denied') {
    window.location.replace('/access-denied');
    throw new ApiError(403, 'Access denied — redirecting');
  }
}

/**
 * 503 is the server's maintenance-mode response. The MaintenanceGuard
 * polls /v1/maintenance and swaps the SPA to the downtime screen
 * within seconds — toasting every queued in-flight request just adds
 * "Action failed (503)" noise on top of the downtime screen. Tests
 * `res.status === 503` so we silently drop the toast and let the
 * maintenance gate take over.
 */
function isMaintenance(res: Response): boolean {
  return res.status === 503;
}

const API_BASE = '/v1';

// Module-level singleton guard: dedupes a NEW-conversation send against a
// truly concurrent duplicate — a StrictMode double-effect or a rapid double
// navigation firing in the same tick, both of which would otherwise create two
// conversations. Existing-conversation sends (conversationId set) always pass.
// The guard is held ONLY across `createConversation` — the one race window
// where two calls could each create a separate conversation — and released the
// instant the conversation exists. So a genuinely different new chat started
// while this one is still streaming is never blocked.
let chatStreamInFlight = false;

/** Reset the new-conversation guard. Exposed so an intentional fresh
 *  navigation (e.g. Home → /chats via a Quick Action) can clear a stale lock
 *  before starting its own new chat. */
export function resetChatStreamGuard(): void {
  chatStreamInFlight = false;
}

// Reset on HMR so a stale lock from a previous module version doesn't block.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    resetChatStreamGuard();
  });
}

/**
 * JSON request headers with the platform JWT bearer. `minValidityMs` is passed through to
 * `ensureFreshPlatformJwt`; requests that start an agent turn ask for `AGENT_TURN_MIN_VALIDITY_MS`.
 */
async function headers(
  minValidityMs?: number,
): Promise<Record<string, string>> {
  // Identity travels only as the platform JWT bearer — the gateway and every service verify
  // it against cti-identity-service's JWKS, stripping any client-supplied identity headers.
  // ensureFreshPlatformJwt silently renews the platform JWT when it is missing or near expiry
  // (15 min TTL) — Teams SSO in a Tab, Entra in a browser — so a long-lived tab doesn't decay
  // into 401s.
  const h: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const jwt = await ensureFreshPlatformJwt(minValidityMs);
  if (jwt) h['Authorization'] = `Bearer ${jwt}`;
  return h;
}

export async function fetchConversations(
  search?: string,
  projectId?: string | null,
  opts?: {
    limit?: number;
    offset?: number;
    status?: 'archived';
    workspaceId?: string | null;
  },
): Promise<Conversation[]> {
  const params = new URLSearchParams();
  if (search && search.trim()) params.set('search', search.trim());
  // Backend reads `project_id` (snake_case); sending `projectId` was silently ignored,
  // so a project view returned EVERY conversation. Omit the param when no project id is
  // given (null/undefined) → unfiltered list, the prior behavior for the main view.
  if (typeof projectId === 'string' && projectId.length > 0)
    params.set('project_id', projectId);
  // Scope the list to the selected workspace (global-workspaces phase 2). The DTO key is
  // `workspace_id` (snake_case); with it set the service applies workspace membership + visibility.
  // Omitted when null/undefined → the caller's own cross-workspace conversations (legacy list).
  if (typeof opts?.workspaceId === 'string' && opts.workspaceId.length > 0)
    params.set('workspace_id', opts.workspaceId);
  if (opts?.status === 'archived') params.set('status', 'archived');
  if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts?.offset !== undefined) params.set('offset', String(opts.offset));
  const qs = params.toString();
  const url = qs
    ? `${API_BASE}/conversations?${qs}`
    : `${API_BASE}/conversations`;
  const res = await fetch(url, { headers: await headers() });
  checkAccessDenied(res);
  if (!res.ok) {
    if (!isMaintenance(res)) {
      notify.error(`Failed to load conversations (${res.status})`);
    }
    throw new ApiError(res.status, `fetchConversations failed: ${res.status}`);
  }
  const data: unknown = await res.json();
  return z.array(ConversationSchema).parse(data) as Conversation[];
}

/**
 * Fetch a single conversation by id (`GET /v1/conversations/:id`). Unlike the list, this returns a
 * conversation regardless of the currently-selected workspace, so callers can read the chat's own
 * `workspaceId` / `projectId` even when it sits outside the scoped Recents cache (a deep-link into
 * another workspace, or a legacy null-workspace chat).
 */
export async function fetchConversation(id: string): Promise<Conversation> {
  const res = await fetch(`${API_BASE}/conversations/${id}`, {
    headers: await headers(),
  });
  checkAccessDenied(res);
  if (!res.ok)
    throw new ApiError(res.status, `fetchConversation failed: ${res.status}`);
  const data: unknown = await res.json();
  return ConversationSchema.parse(data) as Conversation;
}

async function transitionConversation(
  id: string,
  action: 'archive' | 'restore' | 'undelete',
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/conversations/${encodeURIComponent(id)}/${action}`,
    { method: 'POST', headers: await headers() },
  );
  checkAccessDenied(res);
  if (!res.ok) {
    throw new ApiError(
      res.status,
      `${action}Conversation failed: ${res.status}`,
    );
  }
}

export async function archiveConversation(id: string): Promise<void> {
  return transitionConversation(id, 'archive');
}

export async function restoreConversation(id: string): Promise<void> {
  return transitionConversation(id, 'restore');
}

/** Undo a delete (MEDA-1326). The server refuses once the chat is past its retention window. */
export async function undeleteConversation(id: string): Promise<void> {
  return transitionConversation(id, 'undelete');
}

export async function createConversation(opts?: {
  projectId?: string | null;
  title?: string;
  workspaceId?: string | null;
}): Promise<Conversation> {
  const body: Record<string, unknown> = {
    // Backend DTO field is `project_id`; `projectId` was stripped by the validation
    // whitelist, so new chats were never linked to their project (→ project
    // instructions never applied). snake_case matches the API contract.
    project_id: opts?.projectId ?? null,
    title: opts?.title,
  };
  // Scope the new chat to the selected workspace (global-workspaces phase 2). The DTO key is
  // `workspace_id` (snake_case); sending `workspaceId` would be dropped by the validation
  // whitelist, leaving the chat at `workspace_id=NULL` so it vanishes from the workspace-scoped
  // Recents. Omitted when null/empty → the service's legacy null-workspace behavior.
  if (typeof opts?.workspaceId === 'string' && opts.workspaceId.length > 0)
    body.workspace_id = opts.workspaceId;
  const res = await fetch(`${API_BASE}/conversations`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(body),
  });
  checkAccessDenied(res);
  if (!res.ok) throw new Error(`Failed to create conversation: ${res.status}`);
  return res.json();
}

/**
 * Conversation ids pre-created for attachment uploads before the user's first message exists.
 * The server gives them the generic default title; the first send into them retitles via
 * `applyReservedConversationTitle`. Module-level (like the stream guard) because the reservation
 * and the send that consumes it can happen in different routes/hook instances (the Home
 * launcher reserves, /chats sends).
 */
const reservedUntitledConversationIds = new Set<string>();

/**
 * Create a conversation before its first message exists. Attachment uploads must be keyed to a
 * conversation id ({user}/{conversationId}/{file} on S3), so launcher composers reserve one up
 * front — at which point no message text is available and the title can only be the server
 * default. The id is remembered so the first send can retitle the conversation from the message.
 */
export async function reserveConversation(opts?: {
  projectId?: string | null;
  workspaceId?: string | null;
}): Promise<Conversation> {
  const created = await createConversation(opts);
  reservedUntitledConversationIds.add(created.id);
  return created;
}

/**
 * Retitle a reserved (pre-created, default-titled) conversation from its first user message,
 * with the same derivation brand-new chats get (`deriveConversationTitle`: text first, then
 * attachment names, then skill titles). No-op for conversations that were never reserved — their title
 * either came from `createConversation` or belongs to the user. Returns the applied title, or
 * null when nothing was patched. The id leaves the set before the PATCH is awaited (so
 * concurrent sends retitle at most once) and returns on failure (so the next send retries a
 * transient error). A user renaming the reserved chat before the first send consumes the
 * reservation inside `updateConversation`, so their title is never clobbered.
 */
export async function applyReservedConversationTitle(
  conversationId: string,
  message: string,
  attachments?: OutgoingAttachment[],
  skillTitles?: string[],
): Promise<string | null> {
  if (!reservedUntitledConversationIds.has(conversationId)) return null;
  reservedUntitledConversationIds.delete(conversationId);
  const title = deriveConversationTitle(message, attachments, skillTitles);
  try {
    await updateConversation(conversationId, { title });
  } catch (patchError) {
    reservedUntitledConversationIds.add(conversationId);
    throw patchError;
  }
  return title;
}

/**
 * The server hard-caps `limit` at 200 (conversations.service.listMessages) and
 * pages oldest-first (`sequence ASC`). The SPA renders the WHOLE conversation
 * — it has no scroll-back pagination — so we must fetch every page, not just
 * the first. Sending no limit let the server default to page 1 / limit 50: any
 * conversation past 50 messages silently lost its NEWEST turns (they sit beyond
 * the first page), so the visible tail froze one-or-more turns behind and the
 * missing turns never came back, not even on reload. Request full 200-row pages
 * and keep going until a short page proves we've read the tail.
 */
const MESSAGES_PAGE_SIZE = 200;

/**
 * Hard cap on the page walk (200 × 500 = 100k messages — far beyond any real
 * conversation). A backstop only: the loop normally ends on a short page or the
 * no-progress guard below. It exists so a pathological backend can never loop
 * without bound.
 */
const MESSAGES_MAX_PAGES = 500;

/**
 * Load the complete message history for a conversation, walking every server
 * page. The common case (a conversation under 200 messages) is a single
 * request — identical cost to before. Dedups by id so a message appended
 * between page fetches (offset shift on an actively-growing long conversation)
 * can't produce a duplicate row.
 *
 * Termination: a short (< page-size) page means we've read the tail. As a
 * safety net for a backend that ignores `page` (or a proxy serving a cached
 * page 1), a full page that contributes NO new ids means we're re-reading the
 * same page — stop immediately rather than fetching the same rows up to the
 * hard cap. Either way the UI never hangs on an unbounded fetch loop.
 *
 * `signal` goes to every page request, so aborting it cancels the read in flight.
 */
export async function fetchMessages(
  conversationId: string,
  signal?: AbortSignal,
): Promise<Message[]> {
  const byId = new Map<string, Message>();
  const requestHeaders = await headers();
  for (let page = 1; page <= MESSAGES_MAX_PAGES; page++) {
    const res = await fetch(
      `${API_BASE}/conversations/${conversationId}/messages?page=${page}&limit=${MESSAGES_PAGE_SIZE}`,
      { headers: requestHeaders, signal },
    );
    checkAccessDenied(res);
    if (!res.ok) {
      /**
       * A 404 is not a transient failure: the conversation is gone for this viewer — unshared
       * under them, or deleted. The 4 s poll would repeat it forever, so it stays silent here
       * and `useChat` raises the "no longer available" notice once instead.
       */
      const isConversationGone = res.status === 404;
      /** A 401 is the session's business: renewal retries it or ends the session, never a toast. */
      const isUnauthorized = res.status === 401;
      if (!isMaintenance(res) && !isConversationGone && !isUnauthorized) {
        notify.error(`Failed to load messages (${res.status})`);
      }
      throw new ApiError(res.status, `fetchMessages failed: ${res.status}`);
    }
    const data: unknown = await res.json();
    const pageMessages: Message[] = z
      .array(MessageSchema)
      .parse(data)
      .map(({ turn_seq, cancel_requested, ...message }) => ({
        ...message,
        ...(turn_seq === undefined ? {} : { turnSeq: turn_seq }),
        ...(cancel_requested === true ? { cancelRequested: true } : {}),
      })) as Message[];
    const sizeBefore = byId.size;
    for (const message of pageMessages) byId.set(message.id, message);
    if (pageMessages.length < MESSAGES_PAGE_SIZE) break;
    if (byId.size === sizeBefore) {
      // eslint-disable-next-line no-console
      console.warn(
        `[integration-api] fetchMessages got a full page with no new ids for ${conversationId}; the backend is not honoring page — stopping with a partial history`,
      );
      break;
    }
  }
  return [...byId.values()];
}

/**
 * The address of a document in an external system, as "put it in the chat" hands it over. Only the pair
 * that names the document is required: `connectionId` says which of the caller's own connections to reach
 * it through and may be left out, in which case their first one answers, and `siteId` / `webUrl` are
 * recorded as provenance and grant nothing.
 */
export interface ChatFileAddress {
  connectionId?: string;
  siteId?: string;
  driveId: string;
  itemId: string;
  webUrl?: string;
}

/** The file a document became: an artifact of this conversation, plus the moment its snapshot was taken. */
export interface ImportedChatFile {
  artifactId: string;
  name: string;
  contentType: string;
  size: number;
  webUrl?: string;
  importedAt: string;
}

const ImportedChatFileSchema = z
  .object({
    artifact_id: z.string().trim().min(1),
    name: z.string().trim().min(1),
    content_type: z.string().trim().min(1).nullable(),
    size: z.number().finite().nonnegative(),
    web_url: z.string().trim().min(1).nullable(),
    imported_at: z.string().trim().min(1),
  })
  .strict();

/** Exact address of a personal OneDrive file selected for explicit materialization. */
export interface OneDriveChatFileAddress {
  source: 'onedrive';
  scope_id: string;
  item_id: string;
}

/** Copy the current bytes of a personal OneDrive file into a conversation artifact. */
export async function shareOneDriveCopy(
  conversationId: string,
  address: OneDriveChatFileAddress,
): Promise<ImportedChatFile> {
  const res = await fetch(
    `${API_BASE}/conversations/${encodeURIComponent(conversationId)}/files`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        source: 'onedrive',
        scope_id: address.scope_id,
        item_id: address.item_id,
      }),
    },
  );
  if (!res.ok) {
    throw await apiErrorFromResponse(res, 'Could not share this OneDrive copy');
  }
  const parsed = ImportedChatFileSchema.safeParse(
    await res.json().catch(() => null),
  );
  if (!parsed.success) {
    throw new ApiError(
      502,
      'Could not share this OneDrive copy because the server returned an invalid artifact.',
    );
  }
  const body = parsed.data;
  return {
    artifactId: body.artifact_id,
    name: body.name,
    contentType: body.content_type ?? 'application/octet-stream',
    size: body.size,
    ...(body.web_url ? { webUrl: body.web_url } : {}),
    importedAt: body.imported_at,
  };
}

/**
 * Put one document from an external system into this conversation as a file of the chat
 * (`POST /v1/conversations/:id/files`).
 *
 * Nothing calls this on its own, and that is the design: a person clicks it on a file the agent pointed
 * at. Copying every document the model reads would turn reading into a silent transfer of a customer's
 * material into our storage (canon §10), so the import has exactly one trigger, and it is a human one.
 *
 * The body is the wire's own snake_case. `connection_id` matters most of all: the caller may hold several
 * connections of the provider, and the document exists only in one of them — the same `drive_id` in
 * another tenant is either nothing or a different document.
 */
export async function putFileInChat(
  conversationId: string,
  address: ChatFileAddress,
): Promise<ImportedChatFile> {
  const res = await fetch(
    `${API_BASE}/conversations/${encodeURIComponent(conversationId)}/files`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        ...(address.connectionId
          ? { connection_id: address.connectionId }
          : {}),
        ...(address.siteId ? { site_id: address.siteId } : {}),
        drive_id: address.driveId,
        item_id: address.itemId,
        ...(address.webUrl ? { web_url: address.webUrl } : {}),
      }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new ApiError(
      res.status,
      extractErrorMessage(detail) ||
        `Could not put this file in the chat (${res.status})`,
    );
  }
  const body = (await res.json()) as {
    artifact_id?: string;
    name?: string;
    content_type?: string;
    size?: number;
    web_url?: string | null;
    imported_at?: string;
  };
  return {
    artifactId: body.artifact_id ?? '',
    name: body.name ?? '',
    contentType: body.content_type ?? '',
    size: typeof body.size === 'number' ? body.size : 0,
    ...(body.web_url ? { webUrl: body.web_url } : {}),
    importedAt: body.imported_at ?? '',
  };
}

/**
 * DELETE a conversation. A confirmed delete also drops the pending title reservation; on a
 * failed response the reservation stays, so a conversation that still exists keeps its
 * first-send retitle. The fire-and-forget (no-throw) contract is unchanged; the answer says
 * whether the delete was confirmed, for a caller that does more only after it (MEDA-1150).
 */
export async function deleteConversation(id: string): Promise<boolean> {
  const res = await fetch(`${API_BASE}/conversations/${id}`, {
    method: 'DELETE',
    headers: await headers(),
  });
  if (res.ok) reservedUntitledConversationIds.delete(id);
  return res.ok;
}

/**
 * PATCH a conversation. Any successful title write — the automatic first-send retitle or a
 * manual rename — consumes the pending reservation (see `reserveConversation`), so a later
 * send never overwrites the applied title.
 */
export async function updateConversation(
  id: string,
  patch: { title?: string; pinned?: boolean },
): Promise<void> {
  const res = await fetch(`${API_BASE}/conversations/${id}`, {
    method: 'PATCH',
    headers: await headers(),
    body: JSON.stringify(patch),
  });
  checkAccessDenied(res);
  if (!res.ok) throw new Error(`Failed to update conversation: ${res.status}`);
  if (patch.title !== undefined) reservedUntitledConversationIds.delete(id);
}

/**
 * Set a conversation's audience (global-workspaces phase 4). `visibility` is the shared-thread level
 * — `private` (author only), `group` (the listed participants), `public` (every workspace member).
 * `participantIds` is the explicit member set for `group` (ignored server-side for private/public,
 * where the audience is derived). The wire body is snake_case (`visibility`, `participant_ids`) to
 * match the service DTO — a camelCase key would be dropped by the service's validation whitelist.
 *
 * The web sends both keys; persisting `participant_ids` (and `visibility` on PATCH) requires the
 * matching conversation-service fields — see the phase-4 follow-up. A member sends `participantIds`
 * only for `group`.
 */
export async function updateConversationAudience(
  id: string,
  audience: {
    visibility: 'private' | 'group' | 'public';
    participantIds?: string[];
  },
): Promise<void> {
  const body: Record<string, unknown> = { visibility: audience.visibility };
  if (audience.visibility === 'group') {
    body.participant_ids = audience.participantIds ?? [];
  }
  const res = await fetch(`${API_BASE}/conversations/${id}`, {
    method: 'PATCH',
    headers: await headers(),
    body: JSON.stringify(body),
  });
  checkAccessDenied(res);
  if (!res.ok) {
    throw await apiErrorFromResponse(
      res,
      'Failed to update conversation audience',
    );
  }
}

/**
 * Mark a conversation as read. Server stamps `lastReadAt = NOW()` so the
 * sidebar unread-dot (server-computed `unread` field) clears on the next
 * conversations fetch. Fire-and-forget — failures are logged but don't
 * surface to the UI: the dot will just stick around until the next read
 * attempt succeeds, which is acceptable degraded behaviour for a UX hint.
 */
export async function markConversationRead(id: string): Promise<void> {
  try {
    const res = await fetch(`${API_BASE}/conversations/${id}/read`, {
      method: 'POST',
      headers: await headers(),
    });
    checkAccessDenied(res);
    if (!res.ok && res.status !== 204) {
      // eslint-disable-next-line no-console
      console.warn(`[markConversationRead] ${id} → ${res.status}`);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[markConversationRead] ${id} failed`, err);
  }
}

/**
 * Cancel an in-flight assistant turn. Idempotent; safe to call after the stream already finished.
 *
 * The route is conversation-scoped — `POST /v1/conversations/:cid/messages/:mid/cancel` — because
 * the service refuses to cancel by message id alone: without the conversation in the path, a caller
 * with write access to one chat could cancel a message in someone else's. The flat form is a plain
 * 404 that cancels nothing, which is exactly how Stop stayed dead in prod.
 *
 * Returns whether the server ACCEPTED the cancel. `false` is not an error to show the user — a 404
 * legitimately means the run already ended. It means only that the server will not be ending this
 * turn on the caller's behalf, which is what decides whether the caller still has to close the
 * stream itself. Reporting every non-2xx as success is what hid the dead button.
 */
export async function cancelRun(
  conversationId: string,
  messageId: string,
): Promise<boolean> {
  try {
    const res = await fetch(
      `${API_BASE}/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/cancel`,
      { method: 'POST', headers: await headers() },
    );
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.warn(`[cancelRun] ${messageId} → ${res.status}`);
    }
    return res.ok;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[cancelRun] ${messageId} failed`, err);
    return false;
  }
}

/**
 * Base path for the workspace-scoped connector routes (`cti-identity-service`, reached via the
 * gateway). Connectors are a workspace resource (global-workspaces phase 2.5): status, authorize,
 * disconnect and api-key connect all target `/v1/connectors/workspaces/:workspaceId/…`, scoped to
 * the currently selected workspace so a surface never shows or writes another workspace's or a bare
 * per-user connection.
 */
const WORKSPACE_CONNECTORS_BASE = `${API_BASE}/connectors/workspaces`;

/**
 * The selected workspace id from the shared UI store, read imperatively so the plain (non-hook)
 * connector helpers below can scope to it. The React hooks that wrap them (`useConnections`) key
 * their query by the same id so they refetch when the switcher changes it.
 */
function currentWorkspaceId(): string | null {
  return useUiStore.getState().selectedWorkspaceId;
}

/**
 * Same as `currentWorkspaceId` but throws when nothing is selected — for the authorize/connect/
 * disconnect write paths that cannot target a workspace route without an id.
 */
function requireWorkspaceId(): string {
  const workspaceId = currentWorkspaceId();
  if (!workspaceId) throw new Error('No workspace selected');
  return workspaceId;
}

/**
 * A connector request the service refused, with the status it refused on.
 *
 * The status matters to the caller and a plain `Error` throws it away: a 409 from `authorize` means an
 * existing connection is missing its name or its instruction, which is a form the person can fix on the
 * spot, while every other 4xx is a dead end that only deserves the message. `message` is the service's
 * own text, kept verbatim — it names the connection and the field, which no re-wording here could.
 */
export class ConnectorRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ConnectorRequestError';
  }
}

/**
 * Fetch the OAuth authorize URL for a workspace connector. `permission_level` and `allow_delete` carry
 * the access the person chose; a level the caller does not name is left off, so identity applies its
 * own rule (defaults for a new connection, the stored value on a reconnect) and nothing widens by
 * accident. Power BI always sends `read` and `false`, because it only reads.
 * The callback binds the new connection to the workspace
 * (`auth_states.workspace_id`), so — unlike the old per-user route — no `conversationId` is threaded.
 *
 * `connectionId` names an existing connection to RE-connect, which is how a reconnect keeps the same
 * OAuth application and the same tenant instead of creating a second connection. Left out, the
 * callback creates one.
 *
 * `label` and `usageHint` ride through the OAuth round trip so the new connection is born named. They
 * are asked for before the popup opens precisely because there is no second chance: the callback
 * creates the row, and an unnamed second connection of a provider is what the service refuses with a
 * 409. Empty strings are left off rather than sent as blanks.
 *
 * `externalTenantId` connects a customer's own directory: it names the directory whose administrator
 * has approved us, and the service picks the multi-tenant application from it. A directory with no
 * approval on record is refused there, so this is never a way to reach one. A connect into a customer
 * directory carries the name and the instruction like any other, so that lane cannot be the one that
 * creates an unnamed second connection.
 */
export async function getAuthUrl(
  provider: string,
  opts?: {
    permissionLevel?: ConnectorPermissionLevel;
    allowDelete?: boolean;
    connectionId?: string | null;
    externalTenantId?: string | null;
    label?: string;
    usageHint?: string;
  },
  workspaceIdArg?: string | null,
): Promise<{ auth_url: string; attempt_id?: string }> {
  if (
    provider === 'powerbi' &&
    getRuntimeConfig().connectors.powerbi !== true
  ) {
    throw new ConnectorRequestError('Power BI is unavailable.', 403);
  }
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const writable = supportsConnectorWrite(provider);
  const permissionLevel = writable ? opts?.permissionLevel : 'read';
  const allowDelete = writable ? opts?.allowDelete : false;
  const params = new URLSearchParams();
  if (permissionLevel) params.set('permission_level', permissionLevel);
  if (allowDelete !== undefined)
    params.set('allow_delete', String(allowDelete));
  if (opts?.connectionId) params.set('connection_id', opts.connectionId);
  if (opts?.externalTenantId) {
    params.set('external_tenant', opts.externalTenantId);
  }
  if (opts?.label?.trim()) params.set('label', opts.label.trim());
  if (opts?.usageHint?.trim()) {
    params.set('usage_hint', opts.usageHint.trim());
  }
  const query = params.toString();
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/${encodeURIComponent(
      provider,
    )}/authorize${query ? `?${query}` : ''}`,
    { headers: await headers() },
  );
  if (!res.ok) {
    // Server returns Nest's standard `{ statusCode, message, error }` shape
    // for 4xx errors. Surface that message verbatim so the UI can show the
    // exact reason (e.g. "Salesforce OAuth is not configured…").
    let detail = `Failed to get auth URL (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (Array.isArray(body.message)) detail = body.message.join('; ');
      else if (typeof body.message === 'string') detail = body.message;
    } catch {
      /* keep default detail */
    }
    throw new ConnectorRequestError(detail, res.status);
  }
  return res.json();
}

/** Where one OAuth connect stands, as identity reports it. */
export type ConnectAttemptStatus = 'pending' | 'connected' | 'failed';

/** One OAuth connect's outcome: the connection it wrote once connected, the reason once failed. */
export interface ConnectAttempt {
  status: ConnectAttemptStatus;
  connectionId: string | null;
  permissionLevel: ConnectorPermissionLevel | null;
  /** The Allow delete switch the connection landed with; absent when identity did not send it. */
  allowDelete?: boolean;
  message: string | null;
}

/**
 * Asks identity how the connect named by `attemptId` ended. The connect waits on this rather than
 * guessing its new connection from presence, which broke every time presence changed what its fields
 * mean. An unknown status reads as `pending`, so a server newer than this build cannot end a wait by
 * sending a value the wait does not understand.
 */
export async function getConnectAttempt(
  attemptId: string,
): Promise<ConnectAttempt> {
  const res = await fetch(
    `${API_BASE}/connectors/attempts/${encodeURIComponent(attemptId)}`,
    { headers: await headers() },
  );
  if (!res.ok) {
    throw new ConnectorRequestError(
      `Failed to read the connect attempt (${res.status})`,
      res.status,
    );
  }
  const body = (await res.json()) as {
    status?: string;
    connection_id?: string | null;
    permission_level?: string | null;
    allow_delete?: boolean | null;
    message?: string | null;
  };
  const status: ConnectAttemptStatus =
    body.status === 'connected' || body.status === 'failed'
      ? body.status
      : 'pending';
  return {
    status,
    connectionId: body.connection_id ?? null,
    permissionLevel:
      body.permission_level === 'read' || body.permission_level === 'read_write'
        ? body.permission_level
        : null,
    ...(typeof body.allow_delete === 'boolean'
      ? { allowDelete: body.allow_delete }
      : {}),
    message: body.message ?? null,
  };
}

/**
 * The link a customer's administrator follows to approve us for their whole directory. The member
 * sends it on themselves: we have no channel into another organization, and a link they can see and
 * paste is an honest promise where "we emailed them" would not be.
 *
 * A writer's route. The approval comes back to the platform's own callback, so the answer shows up in
 * the presence read rather than here.
 */
export async function getAdminConsentUrl(
  provider: string,
  workspaceIdArg?: string | null,
): Promise<{ consent_url: string }> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/${encodeURIComponent(
      provider,
    )}/admin-consent-url`,
    { headers: await headers() },
  );
  if (!res.ok) {
    let detail = `Failed to get the approval link (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (Array.isArray(body.message)) detail = body.message.join('; ');
      else if (typeof body.message === 'string') detail = body.message;
    } catch {
      /* keep default detail */
    }
    throw new Error(detail);
  }
  return res.json();
}

/**
 * Built-in connector ids the directory always shows a status for — the floor of the status map, so
 * a known connector reads `false` even when the workspace presence list omits it. Providers the
 * server catalog adds beyond this list (e.g. browserstack) are NOT enumerated here; their status is
 * overlaid from the presence response in `getAuthStatus`, so a saved connection reads connected
 * regardless of membership in this list. The `satisfies` check keeps the floor to strict
 * `BuiltinConnectorId`s (the v1 union; `ConnectorId` widens to `| string` in v2).
 */
const STATUS_PROVIDERS = [
  'jira',
  'confluence',
  'salesforce',
  'testrail',
  'zephyr',
  'outlook',
  'teams',
  'azure_devops',
  'github',
  'gitlab',
  'sharepoint',
  'powerbi',
] as const satisfies readonly BuiltinConnectorId[];

/**
 * Per-provider connected map for the selected workspace, derived from the workspace presence list
 * (`GET /v1/connectors/workspaces/:workspaceId`). The `STATUS_PROVIDERS` floor guarantees every
 * built-in reads `false` when absent; every provider the response carries (including catalog-only
 * ones like browserstack) then overlays its real connected flag, so a just-saved api-key connector
 * flips to connected. With no workspace selected every provider reads disconnected (a surface that
 * renders before the switcher resolves must not throw). On a non-OK response the floor is returned;
 * a network-level fetch rejection still throws.
 */
export async function getAuthStatus(
  workspaceIdArg?: string | null,
): Promise<ConnectorStatusMap> {
  const workspaceId =
    workspaceIdArg === undefined ? currentWorkspaceId() : workspaceIdArg;
  if (!workspaceId) {
    return Object.fromEntries(
      STATUS_PROVIDERS.map((p) => [p, false]),
    ) as ConnectorStatusMap;
  }
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}`,
    {
      headers: await headers(),
    },
  );
  // 403 → redirect to /access-denied (consistent with the other authed calls),
  // rather than silently reporting every connector as disconnected.
  checkAccessDenied(res);
  const connectors: { provider: string; connected: boolean }[] = res.ok
    ? ((
        (await res.json()) as {
          connectors?: { provider: string; connected: boolean }[];
        }
      ).connectors ?? [])
    : [];
  // Null-prototype map: server-provided provider keys are overlaid below, so a
  // key like `__proto__` must not reach Object.prototype (prototype pollution).
  const statuses: Record<string, boolean> = Object.create(null);
  for (const provider of STATUS_PROVIDERS) statuses[provider] = false;
  for (const connector of connectors) {
    if (
      connector.provider === 'powerbi' &&
      getRuntimeConfig().connectors.powerbi !== true
    ) {
      continue;
    }
    statuses[connector.provider] = connector.connected;
  }
  return statuses as ConnectorStatusMap;
}

/**
 * Where a connected provider resolved from — mirrors identity's `resolved_from`.
 * `'workspace'` is a connection owned by this workspace (the caller's own row or
 * the shared service-account); `'personal'` is the caller's personal-workspace
 * connection surfaced here only as an inherited fallback; `null` when nothing is
 * connected.
 */
export type ConnectorResolvedFrom = 'workspace' | 'personal' | null;

/**
 * Normalizes the server's `resolved_from` to the closed union. Responses are
 * type-cast, not validated, so a newer server value (e.g. an org tier) must not
 * flow through as-is and silently break the union consumers (the OAuth poll
 * keys on `'workspace'`). Anything unrecognized becomes null.
 */
function normalizeResolvedFrom(value: unknown): ConnectorResolvedFrom {
  return value === 'workspace' || value === 'personal' ? value : null;
}

/**
 * Normalizes the server's `permission_level` to the closed union. Same API-cast reasoning as
 * {@link normalizeResolvedFrom}: an absent or unrecognized value reads as `null` — "unknown", never
 * a grant a surface could act on.
 */
function normalizePermissionLevel(
  value: unknown,
): ConnectorPermissionLevel | null {
  return value === 'read' || value === 'read_write' ? value : null;
}

/**
 * How far a personal connection travels: `all_workspaces` follows its owner into every shared
 * workspace, `personal_only` stays in the personal one. Meaningless on a workspace-owned connection,
 * where the server ignores it.
 */
export type ConnectorShareScope = 'all_workspaces' | 'personal_only';

/**
 * Normalizes the server's `share_scope` to the closed union. Same API-cast reasoning as
 * {@link normalizeResolvedFrom}: an unrecognized value reads as `null` — "unknown", never a travel
 * rule a surface could act on.
 */
function normalizeShareScope(value: unknown): ConnectorShareScope | null {
  return value === 'all_workspaces' || value === 'personal_only' ? value : null;
}

/**
 * Which lane a NAMED connection sits in — three values, and deliberately not the same union as
 * {@link ConnectorResolvedFrom}.
 *
 * `'workspace'` is the caller's own connection created in this workspace and reachable only here;
 * `'personal'` is their own, travelling in from their personal space; `'service'` is the workspace's
 * service account, which only unattended runs use and which never answers in chat. `null` is a lane
 * this build does not know, so a surface says nothing about it rather than guessing.
 *
 * `resolved_from` keeps its own two-value union: it answers "where did the connection that ANSWERED
 * come from", which never has a service reading.
 */
export type ConnectorConnectionScope =
  'workspace' | 'personal' | 'service' | null;

/**
 * Normalizes a connection's `scope` to the closed three-value union. Same API-cast reasoning as
 * {@link normalizeResolvedFrom}: a server newer than this build must not flow an unknown lane through
 * into a lookup map or an access claim, so anything unrecognized reads as `null`.
 */
function normalizeConnectionScope(value: unknown): ConnectorConnectionScope {
  return value === 'workspace' || value === 'personal' || value === 'service'
    ? value
    : null;
}

/**
 * Where a connection's resolved NAME came from — the three steps of the resolution, in the order
 * identity walks them (canon §3): `'profile'` is this workspace's own profile, `'base'` is the value the
 * owner set on the connection itself, and `'default'` is a name nobody wrote at all, computed from the
 * provider. `null` is a source this build does not know, and a surface then says nothing rather than
 * claiming an origin it cannot vouch for.
 *
 * The client never derives this. Identity resolves the value and names its own step, which is what keeps
 * one answer to "is this inherited" instead of two.
 */
export type ConnectionValueSource = 'profile' | 'base' | 'default' | null;

/**
 * Where a connection's resolved INSTRUCTION came from. The same two real steps as a name, and no
 * `'default'`: an instruction has no computed fallback, so a connection that describes itself nowhere
 * carries `null` here and a `null` `usageHint` beside it.
 */
export type ConnectionHintSource = 'profile' | 'base' | null;

/**
 * Normalizes `label_from` to the closed union. Same API-cast reasoning as {@link normalizeResolvedFrom}:
 * a newer server can name a step this build has never heard of, and an unknown step must read as "no
 * claim" rather than flow into a lookup map.
 */
function normalizeValueSource(value: unknown): ConnectionValueSource {
  return value === 'profile' || value === 'base' || value === 'default'
    ? value
    : null;
}

/**
 * Normalizes `usage_hint_from`. `'default'` is deliberately NOT accepted: an instruction has no computed
 * default, so that value could only be a misunderstanding, and reading it as one of the two real steps
 * would make an unwritten instruction look inherited.
 */
function normalizeHintSource(value: unknown): ConnectionHintSource {
  return value === 'profile' || value === 'base' ? value : null;
}

/**
 * One named connection of a provider in this workspace, as presence reports it. Never a secret.
 *
 * `connectionId` is the handle every action takes: a caller can hold several connections of one
 * provider here, so "the SharePoint connection" is not an address. Disconnect, reconnect, renaming and
 * browsing all name the connection explicitly.
 *
 * Every connection in `connections` belongs to the CALLER — identity resolves nobody else's for them.
 * `scope` says which of their lanes it sits in ({@link ConnectorConnectionScope}); `label` and
 * `usageHint` arrive already resolved for this workspace.
 */
export interface ConnectorConnection {
  connectionId: string;
  label: string;
  scope: ConnectorConnectionScope;
  /**
   * When and what for the agent should use this connection, ALREADY RESOLVED for this workspace by
   * identity — the workspace profile's value where there is one, the connection's own otherwise.
   * `null` means the connection describes itself nowhere. The client never combines the two values
   * itself: that resolution belongs to identity, and a second implementation of it here is a second
   * answer to the same question.
   */
  usageHint: string | null;
  /**
   * Which step of the resolution answered with {@link label} — so the surface that edits the name can
   * say whether it is inherited, and what an edit is about to create. See {@link ConnectionValueSource}.
   */
  labelFrom: ConnectionValueSource;
  /** Which step answered with {@link usageHint}. See {@link ConnectionHintSource}. */
  usageHintFrom: ConnectionHintSource;
  /**
   * The owner let this workspace's unattended runs work through this connection. Quality is the one the
   * switch is named after, and the one a person is deciding about; the workspace's knowledge indexing
   * reads the same flag when a source names no connection of its own.
   */
  backgroundUse: boolean;
  permissionLevel: ConnectorPermissionLevel | null;
  /**
   * The Allow delete switch of this connection. Optional: a presence read from before identity sent it
   * says nothing either way, and a surface then claims no delete.
   */
  allowDelete?: boolean;
  credentialType: string | null;
  instanceUrl: string | null;
  /** The Entra directory the connection lives in; `null` is the Customertimes tenant. */
  externalTenantId: string | null;
  shareScope: ConnectorShareScope | null;
  /**
   * Identity knows this connection's credential is dead (an expired token or a failed refresh), so a
   * reconnect is due. Optional: a presence read from before identity sent it says nothing either way.
   */
  needsReconnect?: boolean;
}

/**
 * Presence for one provider in the selected workspace: the list of connections the caller reaches,
 * plus the flat fields describing the FIRST of them.
 *
 * The flat fields are what the boolean consumers (quality gates, the knowledge tab's connect gate)
 * read, and they keep the meaning they had before connections were named. Anything that manages a
 * connection reads {@link connections} instead — a second connection is invisible in the flat fields
 * by construction.
 */
export interface ConnectorPresenceEntry {
  connected: boolean;
  resolvedFrom: ConnectorResolvedFrom;
  /**
   * The level the resolved WORKSPACE connection granted, `null` when nothing is connected. This is
   * the only workspace-scoped level the client gets: the catalog's `permission_level` describes the
   * caller's OWN connection, so on a connector the whole workspace shares it reads `read` for every
   * member who did not connect it. Optional so existing presence fixtures stay valid.
   */
  permissionLevel?: ConnectorPermissionLevel | null;
  /**
   * Every connection of this provider the caller reaches here, in the server's resolution order.
   * Empty when nothing is connected. Other members' personal connections are never in it — the
   * server does not resolve them for this caller.
   */
  connections: ConnectorConnection[];
}

/** Per-provider presence for the connectors grid: connected flag + where it resolved. */
export type ConnectorPresenceMap = Record<string, ConnectorPresenceEntry>;

/**
 * The whole presence read: per-provider entries plus one deployment-level fact.
 *
 * `externalTenantLaneAvailable` says whether this deployment holds the multi-tenant SharePoint
 * application, so connecting a customer's own directory can be offered as inactive-with-a-reason
 * rather than hidden. It sits beside the providers, not inside an entry, because it is a property of
 * the deployment and not of a workspace's connections.
 */
export interface ConnectorPresence {
  /** Whether the presence read failed instead of returning a successful empty list. */
  readFailed?: boolean;
  providers: ConnectorPresenceMap;
  externalTenantLaneAvailable: boolean;
  /**
   * The customer directories whose administrators have already approved us for THIS workspace. Empty
   * means the approval has not come back yet — which is the difference between offering to send the
   * link and offering to sign in.
   */
  externalTenantConsents: ExternalTenantConsent[];
}

/** One customer directory approved for the workspace. */
export interface ExternalTenantConsent {
  tenantId: string;
  grantedAt: string | null;
}

/**
 * Richer sibling of {@link getAuthStatus} for the workspace connectors grid: the same presence
 * list, projected to keep each provider's `resolved_from`. The grid needs the workspace-vs-personal
 * distinction to mark an inherited personal connection and withhold a workspace disconnect that
 * would 404; the boolean `getAuthStatus` stays the contract for the OAuth pollers and quality gates.
 * Same floor and no-workspace/non-OK fallbacks as `getAuthStatus`; `readFailed` distinguishes
 * an unavailable read from a successful empty list.
 */
export async function getConnectorPresence(
  workspaceIdArg?: string | null,
): Promise<ConnectorPresence> {
  const workspaceId =
    workspaceIdArg === undefined ? currentWorkspaceId() : workspaceIdArg;
  // Null-prototype map: server-provided provider keys are overlaid below, so a
  // key like `__proto__` must not reach Object.prototype (prototype pollution).
  const floor = (): ConnectorPresenceMap => {
    const map: ConnectorPresenceMap = Object.create(null);
    for (const provider of STATUS_PROVIDERS) {
      map[provider] = {
        connected: false,
        resolvedFrom: null,
        permissionLevel: null,
        connections: [],
      };
    }
    return map;
  };
  if (!workspaceId) {
    return {
      readFailed: true,
      providers: floor(),
      externalTenantLaneAvailable: false,
      externalTenantConsents: [],
    };
  }
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}`,
    {
      headers: await headers(),
    },
  );
  checkAccessDenied(res);
  const body: WorkspacePresenceWire = res.ok
    ? ((await res.json()) as WorkspacePresenceWire)
    : {};
  const providers = floor();
  for (const connector of body.connectors ?? []) {
    if (
      connector.provider === 'powerbi' &&
      getRuntimeConfig().connectors.powerbi !== true
    ) {
      continue;
    }
    providers[connector.provider] = {
      connected: connector.connected,
      resolvedFrom: normalizeResolvedFrom(connector.resolved_from),
      permissionLevel: normalizePermissionLevel(connector.permission_level),
      connections: (connector.connections ?? []).map(connectionFromWire),
    };
  }
  return {
    readFailed: !res.ok,
    providers,
    externalTenantLaneAvailable: body.external_tenant_lane_available === true,
    externalTenantConsents: (body.external_tenant_consents ?? [])
      .filter((consent) => typeof consent.tenant_id === 'string')
      .map((consent) => ({
        tenantId: consent.tenant_id,
        grantedAt: consent.granted_at ?? null,
      })),
  };
}

/**
 * The workspace presence response: the provider list, the deployment-level lane flag, and the
 * customer directories already approved for this workspace.
 */
interface WorkspacePresenceWire {
  connectors?: WorkspaceConnectorPresenceWire[];
  external_tenant_lane_available?: boolean;
  external_tenant_consents?: ExternalTenantConsentWire[];
}

/** One approved customer directory, as identity sends it (snake_case). */
interface ExternalTenantConsentWire {
  tenant_id: string;
  granted_at?: string | null;
}

/** One provider's entry in the workspace presence list, as the service sends it (snake_case). */
interface WorkspaceConnectorPresenceWire {
  provider: string;
  connected: boolean;
  resolved_from?: string | null;
  permission_level?: string | null;
  connections?: ConnectorConnectionWire[];
}

/** One named connection in that entry. The exact snake_case keys identity's presence list sends. */
interface ConnectorConnectionWire {
  connection_id?: string;
  label?: string;
  label_from?: string | null;
  usage_hint?: string | null;
  usage_hint_from?: string | null;
  background_use?: boolean;
  scope?: string | null;
  permission_level?: string | null;
  allow_delete?: boolean | null;
  credential_type?: string | null;
  instance_url?: string | null;
  external_tenant_id?: string | null;
  share_scope?: string | null;
  needs_reconnect?: boolean;
}

/**
 * Maps one connection off the wire. Every union is normalized rather than cast, and `label` falls back
 * to the provider-agnostic word so a row written before labels existed still has something a person
 * can point at.
 */
function connectionFromWire(
  wire: ConnectorConnectionWire,
): ConnectorConnection {
  return {
    connectionId: wire.connection_id ?? '',
    label: wire.label ?? 'Connection',
    usageHint: wire.usage_hint ?? null,
    labelFrom: normalizeValueSource(wire.label_from),
    usageHintFrom: normalizeHintSource(wire.usage_hint_from),
    backgroundUse: wire.background_use === true,
    scope: normalizeConnectionScope(wire.scope),
    permissionLevel: normalizePermissionLevel(wire.permission_level),
    ...(typeof wire.allow_delete === 'boolean'
      ? { allowDelete: wire.allow_delete }
      : {}),
    credentialType: wire.credential_type ?? null,
    instanceUrl: wire.instance_url ?? null,
    externalTenantId: wire.external_tenant_id ?? null,
    shareScope: normalizeShareScope(wire.share_scope),
    ...(wire.needs_reconnect === true ? { needsReconnect: true } : {}),
  };
}

/**
 * The connector catalog — provider metadata (name/vendor/description/icon/authKind). Stays on the
 * per-user route: identity exposes no workspace catalog, and this data is provider-level, not
 * workspace-level. Connectedness for a surface is scoped separately via `getAuthStatus`, which reads
 * the selected workspace's presence list. The catalog's per-user status is used only to decide
 * whether a caller-owned connection row may be mutated.
 */
export async function getConnectorsCatalog(): Promise<ConnectorMeta[]> {
  const res = await fetch(`${API_BASE}/connectors`, {
    headers: await headers(),
  });
  // 403 → redirect to /access-denied (consistent with the other authed calls),
  // rather than silently returning an empty catalog.
  checkAccessDenied(res);
  if (!res.ok) return [];
  const data = (await res.json()) as { connectors?: ConnectorMeta[] };
  return (data.connectors ?? [])
    .filter(
      (connector) =>
        connector.id !== 'powerbi' ||
        getRuntimeConfig().connectors.powerbi === true,
    )
    .map((connector): ConnectorMeta =>
      supportsConnectorWrite(connector.id)
        ? connector
        : { ...connector, authKind: 'oauth', permission_level: 'read' },
    );
}

/**
 * What the api-key connect knows about the connection it is about to create, beside the credential
 * itself. Every field is optional: a first connection of a provider needs no name and no instruction
 * (there is nothing to tell apart).
 *
 * No lane is sent. The service still accepts a `scope`, and it defaults to the caller's own — which is
 * now the only kind of connection the interface creates. A credential belonging to the workspace rather
 * than to a person cannot be made from here: how somebody connects (SSO or a token) is a method, not an
 * access boundary, and Quality reaches a person's own connection as that person instead (MEDA-761).
 */
export interface ConnectionCreateDetails {
  label?: string;
  usageHint?: string;
  /**
   * The connection being RE-connected. It is required on a reconnect and not an optimization: the store
   * no longer overwrites, so a connect that does not name its connection creates a SECOND one beside the
   * live one — which then trips the 409 about an unnamed connection. `null` asks for a new connection.
   */
  connectionId?: string | null;
  /** The Allow edit switch the access dialog confirmed. Left out, identity applies its default. */
  permissionLevel?: ConnectorPermissionLevel;
  /** The Allow delete switch the access dialog confirmed. Left out, identity applies its default. */
  allowDelete?: boolean;
}

/**
 * The extra keys a credentials POST carries, in the wire's own snake_case. Anything the caller left out
 * stays out of the body: the service whitelists it, so a stray or misspelled key is dropped silently
 * and a blank `label` would name the connection with an empty string rather than let the default stand.
 */
function connectionCreateBody(
  details?: ConnectionCreateDetails,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (details?.connectionId) body.connection_id = details.connectionId;
  if (details?.label?.trim()) body.label = details.label.trim();
  if (details?.usageHint?.trim()) body.usage_hint = details.usageHint.trim();
  if (details?.permissionLevel) body.permission_level = details.permissionLevel;
  if (details?.allowDelete !== undefined)
    body.allow_delete = details.allowDelete;
  return body;
}

export interface TestRailCredentialsPayload {
  baseUrl: string;
  email: string;
  apiKey: string;
}

export async function saveTestRailCredentials(
  payload: TestRailCredentialsPayload,
  workspaceIdArg?: string | null,
  details?: ConnectionCreateDetails,
): Promise<void> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/testrail/credentials`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        ...connectionCreateBody(details),
        instance_url: payload.baseUrl,
        username: payload.email,
        api_key: payload.apiKey,
      }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Failed to save TestRail credentials: ${res.status}`,
    );
  }
}

/**
 * Zephyr Scale Cloud region. identity allowlists exactly the two SmartBear
 * API bases — US (the default, `instance_url` omitted) and EU.
 */
export type ZephyrRegion = 'us' | 'eu';

const ZEPHYR_EU_API_BASE = 'https://eu.api.zephyrscale.smartbear.com/v2';

export interface ZephyrCredentialsPayload {
  apiKey: string;
  region: ZephyrRegion;
}

export async function saveZephyrCredentials(
  payload: ZephyrCredentialsPayload,
  workspaceIdArg?: string | null,
  details?: ConnectionCreateDetails,
): Promise<void> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/zephyr/credentials`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        ...connectionCreateBody(details),
        api_key: payload.apiKey,
        // US region is the server default — send instance_url only for EU.
        ...(payload.region === 'eu'
          ? { instance_url: ZEPHYR_EU_API_BASE }
          : {}),
      }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Failed to save Zephyr Scale credentials: ${res.status}`,
    );
  }
}

/**
 * Xray Cloud region. identity allowlists exactly the global default plus the three regional Cloud
 * API hosts (US, EU, AU); the form offers this fixed choice rather than a free-text URL.
 */
export type XrayRegion = 'global' | 'us' | 'eu' | 'au';

const XRAY_API_BASES: Record<XrayRegion, string | undefined> = {
  global: undefined,
  us: 'https://us.xray.cloud.getxray.app/api/v2',
  eu: 'https://eu.xray.cloud.getxray.app/api/v2',
  au: 'https://au.xray.cloud.getxray.app/api/v2',
};

export interface XrayCredentialsPayload {
  clientId: string;
  clientSecret: string;
  region: XrayRegion;
}

/**
 * Xray Cloud runs one global API host plus three regional ones, and identity allowlists exactly
 * those. The form therefore offers a fixed Region choice rather than a free-text URL; `global`
 * sends no instance_url at all, leaving the consumer on its default base.
 */
export async function saveXrayCredentials(
  payload: XrayCredentialsPayload,
  workspaceIdArg?: string | null,
  details?: ConnectionCreateDetails,
): Promise<void> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const instanceUrl = XRAY_API_BASES[payload.region];
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/xray/credentials`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        ...connectionCreateBody(details),
        client_id: payload.clientId,
        client_secret: payload.clientSecret,
        ...(instanceUrl ? { instance_url: instanceUrl } : {}),
      }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Failed to save Xray Cloud credentials: ${res.status}`,
    );
  }
}

export interface Dynamics365CredentialsPayload {
  tenantId: string;
  clientId: string;
  clientSecret: string;
  environmentUrl: string;
}

/**
 * Dynamics 365 connects with a service principal, not OAuth — there is no browser leg, so this is a
 * plain credentials POST like TestRail's rather than an authorize redirect.
 *
 * The environment URL is REQUIRED and is stored on the connection, not configured per deployment:
 * different connections legitimately point at different Dataverse environments (Dev, Test, another
 * customer's tenant entirely), and one CTI deployment serves all of them.
 */
export async function saveDynamics365Credentials(
  payload: Dynamics365CredentialsPayload,
  workspaceIdArg?: string | null,
  details?: ConnectionCreateDetails,
): Promise<void> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/dynamics365/credentials`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        ...connectionCreateBody(details),
        tenant_id: payload.tenantId,
        client_id: payload.clientId,
        client_secret: payload.clientSecret,
        environment_url: payload.environmentUrl,
      }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Failed to save Dynamics 365 credentials: ${res.status}`,
    );
  }
}

export interface DatabricksCredentialsPayload {
  clientId: string;
  clientSecret: string;
  /** The Azure Databricks workspace origin, e.g. https://adb-123.4.azuredatabricks.net */
  workspaceUrl: string;
  /** SQL warehouse every statement runs on unless a tool call names another. */
  warehouseId: string;
}

/**
 * Databricks connects with a service principal, not OAuth — there is no browser leg, so this is a
 * plain credentials POST like Dynamics 365's.
 *
 * Note there is deliberately NO tenant id: this is a **Databricks-native** service principal minted
 * at the workspace's own OIDC endpoint, not an Entra one, despite the workspace being Azure
 * Databricks. A form asking for a tenant is collecting the wrong credential type.
 *
 * The workspace URL and warehouse id are REQUIRED and stored on the connection, not configured per
 * deployment: different connections legitimately point at different customers' workspaces, and one
 * CTI deployment serves all of them.
 */
export async function saveDatabricksCredentials(
  payload: DatabricksCredentialsPayload,
  workspaceIdArg?: string | null,
  details?: ConnectionCreateDetails,
): Promise<void> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/databricks/credentials`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        ...connectionCreateBody(details),
        client_id: payload.clientId,
        client_secret: payload.clientSecret,
        workspace_url: payload.workspaceUrl,
        warehouse_id: payload.warehouseId,
      }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Failed to save Databricks credentials: ${res.status}`,
    );
  }
}

export interface ReportPortalCredentialsPayload {
  /** The Report Portal origin, e.g. https://rp.example.com. Identity stores the origin only. */
  instanceUrl: string;
  /** The Report Portal project key, e.g. `cti_dev`. */
  project: string;
  /** A Report Portal API key, created under Profile → API Keys. */
  apiKey: string;
}

/**
 * Report Portal connects with an API key on a self-hosted instance, so this is a plain credentials
 * POST like TestRail's. The project rides beside the key because a Report Portal key reaches every
 * project its user belongs to, and the connection is pinned to one of them.
 */
export async function saveReportPortalCredentials(
  payload: ReportPortalCredentialsPayload,
  workspaceIdArg?: string | null,
  details?: ConnectionCreateDetails,
): Promise<void> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/reportportal/credentials`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        ...connectionCreateBody(details),
        instance_url: payload.instanceUrl,
        project: payload.project,
        api_key: payload.apiKey,
      }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Failed to save Report Portal credentials: ${res.status}`,
    );
  }
}

export interface AzureDevOpsCredentialsPayload {
  pat: string;
  organization: string;
}

/**
 * Lists the organizations an Azure DevOps PAT can reach, for the connect
 * form's org picker. `null` means the token is org-scoped (discovery is
 * impossible; the form falls back to a manual organization input).
 */
export async function listAzureDevOpsOrganizations(
  pat: string,
): Promise<string[] | null> {
  const res = await fetch(`${API_BASE}/connectors/azure_devops/organizations`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify({ pat }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Failed to list Azure DevOps organizations: ${res.status}`,
    );
  }
  const body = (await res.json()) as { organizations: string[] | null };
  return body.organizations;
}

/**
 * Same discovery with the selected workspace's stored credential — the org picker shown right after
 * an OAuth connect. The Azure DevOps connection is a workspace resource (2.5l), so this reads the
 * workspace's stored PAT via `GET /v1/connectors/workspaces/:workspaceId/azure_devops/organizations`
 * rather than a per-user route. `null` = nothing usable to enumerate.
 */
export async function listMyAzureDevOpsOrganizations(
  workspaceIdArg?: string | null,
): Promise<string[] | null> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/azure_devops/organizations`,
    { method: 'GET', headers: await headers() },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Failed to list Azure DevOps organizations: ${res.status}`,
    );
  }
  const body = (await res.json()) as { organizations: string[] | null };
  return body.organizations;
}

/**
 * Pins the organization of the selected workspace's Azure DevOps connection
 * (`POST /v1/connectors/workspaces/:workspaceId/azure_devops/organization`, body `{ organization }`),
 * so the pin lands on the workspace connection the OAuth flow just wrote — not a per-user record.
 */
export async function setAzureDevOpsOrganization(
  organization: string,
  workspaceIdArg?: string | null,
): Promise<void> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/azure_devops/organization`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({ organization }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Failed to set the Azure DevOps organization: ${res.status}`,
    );
  }
}

/** Stores the caller's Azure DevOps PAT + organization in identity's vault. */
export async function saveAzureDevOpsCredentials(
  payload: AzureDevOpsCredentialsPayload,
  workspaceIdArg?: string | null,
  details?: ConnectionCreateDetails,
): Promise<void> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/azure_devops/credentials`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        ...connectionCreateBody(details),
        pat: payload.pat,
        organization: payload.organization,
      }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Failed to save Azure DevOps credentials: ${res.status}`,
    );
  }
}

export interface GithubCredentialsPayload {
  pat: string;
}

/** Stores the caller's GitHub personal access token in identity's vault. */
export async function saveGithubCredentials(
  payload: GithubCredentialsPayload,
  workspaceIdArg?: string | null,
  details?: ConnectionCreateDetails,
): Promise<void> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/github/credentials`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        ...connectionCreateBody(details),
        pat: payload.pat,
      }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Failed to save GitHub credentials: ${res.status}`,
    );
  }
}

export interface GitlabCredentialsPayload {
  token: string;
  /**
   * GitLab base URL (self-hosted friendly). snake_case verbatim because it
   * mirrors the wire field — the identity DTO requires `instance_url`.
   */
  instance_url: string;
}

/** Stores the caller's GitLab token + instance URL in identity's vault. */
export async function saveGitlabCredentials(
  payload: GitlabCredentialsPayload,
  workspaceIdArg?: string | null,
  details?: ConnectionCreateDetails,
): Promise<void> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/gitlab/credentials`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        ...connectionCreateBody(details),
        token: payload.token,
        instance_url: payload.instance_url,
      }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Failed to save GitLab credentials: ${res.status}`,
    );
  }
}

/** Atlassian credential providers that connect with an API token + base URL. */
export type AtlassianProvider = 'jira' | 'confluence';

export interface AtlassianCredentialsPayload {
  token: string;
  /**
   * Atlassian base URL. snake_case on the wire (`instance_url`) — identity
   * requires it (e.g. `https://your-org.atlassian.net` for Cloud, or the
   * Server/DC base URL).
   */
  instanceUrl: string;
  /**
   * Atlassian account email. Required for Atlassian Cloud (`*.atlassian.net`),
   * omitted for Server/DC. The form decides which; when absent it is left off
   * the request body entirely.
   */
  username?: string;
}

/**
 * Stores a workspace's shared Atlassian (Jira / Confluence) credential in
 * identity's vault. quality's source pickers read it through the workspace
 * connection. Wire body is snake_case: `{ token, instance_url, username? }`.
 */
export async function saveAtlassianCredentials(
  provider: AtlassianProvider,
  payload: AtlassianCredentialsPayload,
  workspaceIdArg?: string | null,
  details?: ConnectionCreateDetails,
): Promise<void> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/${provider}/credentials`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        ...connectionCreateBody(details),
        token: payload.token,
        instance_url: payload.instanceUrl,
        ...(payload.username ? { username: payload.username } : {}),
      }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Failed to save ${provider} credentials: ${res.status}`,
    );
  }
}

export interface BrowserStackCredentialsPayload {
  username: string;
  accessKey: string;
}

/** Stores the caller's BrowserStack username + access key in identity's vault. */
export async function saveBrowserStackCredentials(
  payload: BrowserStackCredentialsPayload,
  workspaceIdArg?: string | null,
  details?: ConnectionCreateDetails,
): Promise<void> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/browserstack/credentials`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        ...connectionCreateBody(details),
        username: payload.username,
        access_key: payload.accessKey,
      }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Failed to save BrowserStack credentials: ${res.status}`,
    );
  }
}

export interface CursorCredentialsPayload {
  token: string;
}

/**
 * Stores the workspace's Cursor API key in identity's vault. Cursor is a plain
 * api-key provider — the only field is the token. Wire body is `{ token }`.
 */
export async function saveCursorCredentials(
  payload: CursorCredentialsPayload,
  workspaceIdArg?: string | null,
  details?: ConnectionCreateDetails,
): Promise<void> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const res = await fetch(
    `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/cursor/credentials`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        ...connectionCreateBody(details),
        token: payload.token,
      }),
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Failed to save Cursor credentials: ${res.status}`,
    );
  }
}

/**
 * Pulls the human-readable `message` out of a NestJS error body so the form
 * shows "Azure DevOps rejected the token…", not raw JSON. Non-JSON bodies
 * pass through as-is.
 */
function extractErrorMessage(detail: string): string {
  try {
    const parsed = JSON.parse(detail) as { message?: string | string[] };
    if (Array.isArray(parsed.message)) return parsed.message.join('; ');
    return parsed.message ?? detail;
  } catch {
    return detail;
  }
}

/**
 * Changes what the agent may do through ONE named connection
 * (`PATCH /v1/connectors/workspaces/:workspaceId/:provider/connections/:connectionId/access`). The body
 * keys are the wire contract verbatim, `permission_level` and `allow_delete`. Only the owner of the
 * connection may change it; the server answers `{connection_id, permission_level, allow_delete}`, which
 * callers ignore because they re-read presence instead.
 */
export async function updateConnectionAccess(
  provider: string,
  connectionId: string,
  access: { permissionLevel: ConnectorPermissionLevel; allowDelete: boolean },
  workspaceIdArg?: string | null,
): Promise<void> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const url = `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/${encodeURIComponent(
    provider,
  )}/connections/${encodeURIComponent(connectionId)}/access`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: await headers(),
    body: JSON.stringify({
      permission_level: access.permissionLevel,
      allow_delete: access.allowDelete,
    }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Could not change the access of this connection (${res.status})`,
    );
  }
}

/**
 * Disconnect ONE named connection of a provider
 * (`DELETE /v1/connectors/workspaces/:id/:provider?connection_id=…`).
 *
 * `connectionId` is required by the service and answers 400 without it: a workspace can hold more than
 * one connection of a provider, so a delete that guesses eventually removes the wrong one. The caller
 * takes the id off the presence entry it drew the card from.
 */
export async function disconnectProvider(
  provider: string,
  connectionId: string,
  workspaceIdArg?: string | null,
): Promise<void> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const url = `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/${encodeURIComponent(
    provider,
  )}?connection_id=${encodeURIComponent(connectionId)}`;
  const res = await fetch(url, { method: 'DELETE', headers: await headers() });
  if (!res.ok) {
    throw new Error(`Failed to disconnect ${provider}: ${res.status}`);
  }
}

/**
 * What a person may change about one of their own connections, as the interface knows it. Every field
 * is optional and an absent one changes nothing — the service reads the body as a subset, so sending
 * only what moved is the contract rather than an optimization.
 */
export interface ConnectionProfilePatch {
  label?: string;
  usageHint?: string;
  /** Whether members of the workspaces the caller owns may choose this connection for Quality. */
  backgroundUse?: boolean;
}

/** What a profile write answers with: the values that LANDED, normalized the way presence does it. */
export interface ConnectionProfile {
  connectionId: string;
  label: string;
  usageHint: string | null;
  backgroundUse: boolean;
  shareScope: ConnectorShareScope | null;
}

/**
 * Builds the PATCH body for a connection profile write. The keys are the wire contract verbatim —
 * `usage_hint` and `background_use` — because the service whitelists the body and drops a camelCase key
 * with no error and no log, which is silent data loss neither TS nor the runtime catches.
 *
 * `share_scope` is deliberately not among them. The route still accepts it and identity still keeps the
 * column, but no surface asks for it any more: a personal connection follows its owner by default, and
 * the switch that used to say otherwise could not explain when turning it off would matter.
 *
 * A field the caller left out is left out of the body: only the values the person actually changed are
 * sent, so a rename cannot quietly clear an instruction it never showed them.
 */
function connectionProfileBody(
  patch: ConnectionProfilePatch,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (patch.label !== undefined) body.label = patch.label;
  if (patch.usageHint !== undefined) body.usage_hint = patch.usageHint;
  if (patch.backgroundUse !== undefined) {
    body.background_use = patch.backgroundUse;
  }
  return body;
}

/**
 * Writes the name, the agent instruction or the Quality permission of ONE named
 * connection (`PATCH /v1/connectors/workspaces/:workspaceId/:provider/connections/:connectionId`).
 *
 * Where the name and the instruction LAND depends on the workspace in the path, and identity decides
 * it: in a working workspace they become that workspace's profile, in the caller's personal workspace
 * they become the connection's own base values. The interface only has to say which of the two the
 * person is looking at.
 *
 * Returns what landed rather than what was asked for, so a card renders the server's answer instead of
 * its own guess. A refusal throws with the server's own message where there is one — the 403 is
 * deliberately not routed through `checkAccessDenied`: it is this connection refusing the change, not
 * the session losing access.
 */
export async function updateConnectionProfile(
  provider: string,
  connectionId: string,
  patch: ConnectionProfilePatch,
  workspaceIdArg?: string | null,
): Promise<ConnectionProfile> {
  const workspaceId = workspaceIdArg ?? requireWorkspaceId();
  const url = `${WORKSPACE_CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/${encodeURIComponent(
    provider,
  )}/connections/${encodeURIComponent(connectionId)}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: await headers(),
    body: JSON.stringify(connectionProfileBody(patch)),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      extractErrorMessage(detail) ||
        `Could not save this connection (${res.status})`,
    );
  }
  const body = (await res.json()) as {
    connection_id?: string;
    label?: string;
    usage_hint?: string | null;
    background_use?: boolean;
    share_scope?: string | null;
  };
  return {
    connectionId: body.connection_id ?? connectionId,
    label: body.label ?? '',
    usageHint: body.usage_hint ?? null,
    backgroundUse: body.background_use === true,
    shareScope: normalizeShareScope(body.share_scope),
  };
}

export async function createUploadUrl(
  req: {
    filename: string;
    contentType: string;
    size: number;
    conversationId?: string;
  },
  authority?: ApiRequestAuthority,
): Promise<{
  uploadUrl: string;
  s3Key: string;
  filename: string;
  contentType: string;
}> {
  authority?.assertCurrent();
  const requestHeaders = await headers();
  authority?.assertCurrent();
  const res = await fetch(`${API_BASE}/uploads/sign`, {
    method: 'POST',
    headers: requestHeaders,
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const err = await res
      .json()
      .catch(() => ({ message: `HTTP ${res.status}` }));
    throw new Error(err.message || `Upload sign failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Same-origin URL of a persisted chat attachment by its stored S3 key. The endpoint is
 * bearer-authed, so a plain `<img src>` (a browser sub-request that can't carry the Authorization
 * header) would 401 — fetch the bytes via `fetchAttachmentBlob` and expose an object URL instead
 * (see `useResolvedAttachmentUrls`).
 */
export function attachmentUrl(key: string): string {
  return `${API_BASE}/uploads/object?key=${encodeURIComponent(key)}`;
}

/**
 * The workspace-artifact content route (`/v1/workspaces/:workspaceId/artifacts/:id/content`, with an
 * optional `?revision=`), built by `workspaceArtifactContentUrl` in `lib/api/workspace-artifacts.ts`.
 * Matched by shape rather than imported so this module keeps no dependency on the artifacts client; the
 * two are pinned together by a test that feeds the builder's output to the predicate below.
 */
const ARTIFACT_CONTENT_URL = new RegExp(
  `^${API_BASE}/workspaces/[^/]+/artifacts/[^/?]+/content(\\?|$)`,
);

const ONEDRIVE_CONTENT_URL = new RegExp(
  `^${API_BASE}/onedrive/scopes/[^/]+/items/[^/]+/content(\\?|$)`,
);

/** Bearer-authenticated current-content route for one personal OneDrive live reference. */
export function oneDriveAttachmentContentUrl(
  scopeId: string,
  itemId: string,
  workspaceId: string,
): string {
  const params = new URLSearchParams({ workspace_id: workspaceId });
  return `${API_BASE}/onedrive/scopes/${encodeURIComponent(scopeId)}/items/${encodeURIComponent(itemId)}/content?${params.toString()}`;
}

/**
 * True for a bearer-authed attachment endpoint that needs the bearer-fetch→object-URL dance (vs a draft
 * `blob:`/`data:` url that an `<img>` can load directly).
 *
 * Both authed shapes count: the uploads object route of a chat upload, and the content route of a
 * workspace artifact. Missing either one is not a type error — the url reaches `<img src>`, the browser
 * sub-request carries no Authorization header, and the user sees a 401.
 */
export function isAttachmentEndpointUrl(url: string): boolean {
  return (
    url.startsWith(`${API_BASE}/uploads/object`) ||
    ARTIFACT_CONTENT_URL.test(url) ||
    ONEDRIVE_CONTENT_URL.test(url)
  );
}

/**
 * A failed attachment byte fetch, carrying the HTTP status so the caller can tell the outcomes apart —
 * a 404 means the object is gone for good, anything else is a failure worth retrying. The status lives
 * in a field, not in the message, so nobody has to parse prose to branch on it.
 */
export class AttachmentFetchError extends Error {
  constructor(public readonly status: number) {
    super(`attachment fetch failed: ${status}`);
    this.name = 'AttachmentFetchError';
  }
}

/** Fetch a bearer-authed attachment as a Blob (consumed by `useResolvedAttachmentUrls`). */
export async function fetchAttachmentBlob(url: string): Promise<Blob> {
  const res = await fetch(url, { headers: await headers() });
  if (!res.ok) {
    throw new AttachmentFetchError(res.status);
  }
  return res.blob();
}

/**
 * Title for a brand-new conversation, derived from the user's opening message. Text wins; when the
 * trimmed text is empty, fall back to the file names, then to the selected skills' titles — a
 * message can be just an image or just a skill, and neither should leave the chat titled the
 * generic "New conversation".
 */
export function deriveConversationTitle(
  message: string,
  attachments?: OutgoingAttachment[],
  skillTitles?: string[],
): string {
  const text = message.trim();
  if (text) return text.slice(0, 60);
  const fileNames = (attachments ?? [])
    .map((attachment) => attachment.name)
    .filter((name): name is string => !!name);
  return (
    summarizeTitleNames(fileNames) ??
    summarizeTitleNames(skillTitles ?? []) ??
    'New conversation'
  );
}

/** The first name capped at 60 characters, with a "+N" count for the rest; null for no names. */
function summarizeTitleNames(names: string[]): string | null {
  if (names.length === 0) return null;
  if (names.length === 1) return names[0]!.slice(0, 60);
  /** Truncate the first name, not the whole string, so the "+N" count is never cut off. */
  const suffix = ` +${names.length - 1}`;
  return `${names[0]!.slice(0, 60 - suffix.length)}${suffix}`;
}

/**
 * Thrown by `streamChat` when the message-creation POST is rejected
 * synchronously, BEFORE any conversation row or stream started (currently:
 * a 422 — e.g. a composer skill chip whose skill was deleted/disabled
 * server-side between selection and send). Distinct from a mid-stream
 * error, which is yielded as a `StreamEvent` instead: nothing here was
 * persisted, so the caller can safely let the user fix their draft and
 * resend rather than treating it as a failed turn.
 */
export class SendRejectedError extends Error {
  constructor(
    message: string,
    public readonly alreadyNotified = false,
  ) {
    super(message);
  }
}

/** A write was rejected because its resource is archived. */
export class ResourceArchivedError extends Error {
  constructor(
    message: string,
    public readonly alreadyNotified = false,
  ) {
    super(message);
  }

  override name = 'ResourceArchivedError';
}

/**
 * The service refused a resume because the caller did not send the message that raised the approval.
 * In a shared thread only that person may answer it; anyone else is told so instead of seeing a
 * status code.
 */
export class ResumeForbiddenError extends Error {
  constructor() {
    super('Only the person whose message raised this approval can answer it.');
  }

  override name = 'ResumeForbiddenError';
}

/**
 * The service refused "Approve & allow similar" with 409 `APPROVAL_NOT_GRANTABLE`: the card offers no
 * rule. Nothing was decided, so the card can still be approved on its own.
 */
export class ApprovalNotGrantableError extends Error {
  constructor() {
    super(
      'This approval cannot be extended to similar operations. Approve it on its own.',
    );
  }

  override name = 'ApprovalNotGrantableError';
}

/** True when a 409 resume answer is the stable `APPROVAL_NOT_GRANTABLE` refusal. */
async function isApprovalNotGrantable(res: Response): Promise<boolean> {
  const body = (await res.json().catch(() => null)) as {
    error?: { code?: unknown };
    code?: unknown;
  } | null;
  return (body?.error?.code ?? body?.code) === 'APPROVAL_NOT_GRANTABLE';
}

/**
 * The service refused the sender's choice on Developer's question, so nothing was decided. `message` is
 * the service's own reason, kept verbatim, since it says what happened: not the sender, Developer
 * unavailable, or the chat moved on (another choice, a newer message, a running turn, archived).
 */
export class DeveloperConfirmationRejectedError extends ApiError {
  override name = 'DeveloperConfirmationRejectedError';
}

/**
 * Where the conversation service routed a send, read from the `routed_to` its answer carries. Anything
 * but a route this build knows reads as none: an older server sends none, a newer one may name another.
 */
function knownMessageRoute(routedTo: unknown): MessageRoute | undefined {
  return routedTo === 'developer' ||
    routedTo === 'developer_task' ||
    routedTo === 'agent'
    ? routedTo
    : undefined;
}

/**
 * The Developer confirmation state a `done` frame names, read from its `developerConfirmation`.
 * Anything but a state this build knows reads as none, so a newer server cannot put an unknown state
 * on the message and draw a card nobody can act on.
 */
function knownDeveloperConfirmation(
  value: unknown,
): DeveloperConfirmation | undefined {
  return value === 'pending' || value === 'developer' || value === 'agent'
    ? value
    : undefined;
}

/** Longer than the server's own 20-second limit on writing a pick into the agent's thread. */
const PICK_SAVE_TIMEOUT_MS = 30_000;
/** Per conversation: the last pick save queued, settled either way. */
const pickSaveQueues = new Map<string, Promise<void>>();
/** Per question: bumped when a Retry drops the pick saves still waiting their turn. */
const droppedPickRounds = new Map<string, number>();

/**
 * Saves the answer variant a person picked for a question. For the last question the server writes
 * it into the agent's thread first, so the next turn continues from it; an earlier question's pick
 * is kept for display. Saves in one conversation go out one at a time and before any later send.
 * Throws when the server refuses, or when a Retry of the question dropped the save before it went out.
 */
export function selectAnswerVariant(
  conversationId: string,
  questionId: string,
  variantId: string,
): Promise<void> {
  const round = droppedPickRounds.get(questionId) ?? 0;
  const previous = pickSaveQueues.get(conversationId) ?? Promise.resolve();
  const save = previous.then(() => {
    if ((droppedPickRounds.get(questionId) ?? 0) !== round)
      throw new Error('A Retry replaced this pick');
    return putActiveVariant(conversationId, questionId, variantId);
  });
  pickSaveQueues.set(
    conversationId,
    save.catch(() => undefined),
  );
  return save;
}

/**
 * Waits for the conversation's pick saves before a send. A Retry of `retriedQuestionId` first drops
 * that question's saves not yet sent, so an older pick cannot land after the new answer. An aborted
 * send stops waiting at once and ends like any aborted send.
 */
function settlePickSaves(
  conversationId: string,
  retriedQuestionId?: string,
  signal?: AbortSignal,
): Promise<void> {
  if (retriedQuestionId)
    droppedPickRounds.set(
      retriedQuestionId,
      (droppedPickRounds.get(retriedQuestionId) ?? 0) + 1,
    );
  const pending = pickSaveQueues.get(conversationId) ?? Promise.resolve();
  if (!signal) return pending;
  return new Promise((resolve, reject) => {
    const abort = () => reject(new DOMException('Aborted', 'AbortError'));
    if (signal.aborted) return abort();
    signal.addEventListener('abort', abort, { once: true });
    void pending.then(() => {
      signal.removeEventListener('abort', abort);
      resolve();
    });
  });
}

async function putActiveVariant(
  conversationId: string,
  questionId: string,
  variantId: string,
): Promise<void> {
  const deadline = new AbortController();
  const timer = setTimeout(() => deadline.abort(), PICK_SAVE_TIMEOUT_MS);
  try {
    const res = await fetch(
      `${API_BASE}/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(questionId)}/active-variant`,
      {
        method: 'PUT',
        headers: await headers(),
        body: JSON.stringify({ variant_id: variantId }),
        signal: deadline.signal,
      },
    ).catch(() => {
      throw new Error('Could not keep this answer (no answer from the server)');
    });
    if (!res.ok) {
      throw new Error(`Could not keep this answer (HTTP ${res.status})`);
    }
  } finally {
    clearTimeout(timer);
  }
}

export async function* streamChat(
  message: string,
  conversationId?: string,
  attachments?: OutgoingAttachment[],
  projectId?: string | null,
  workspaceId?: string | null,
  signal?: AbortSignal,
  opts?: {
    developerReply?: import('../types.js').DeveloperReply;
    skillIds?: string[];
    skillTitles?: string[];
    /** The file open in the viewer beside the chat, sent as `open_file`. */
    openFile?: FileReference;
    /** Retry: answer this stored question again instead of sending a new message. */
    regenerateOf?: string;
  },
): AsyncGenerator<StreamEvent> {
  // Only NEW-conversation sends take the dedup guard; existing-conversation
  // retries (conversationId set) always pass. See the guard's definition above
  // for why it is held only across createConversation.
  const isNewConv = !conversationId;

  if (isNewConv) {
    if (chatStreamInFlight) {
      // eslint-disable-next-line no-console
      console.warn('[streamChat] blocked duplicate new-conversation request');
      return;
    }
    chatStreamInFlight = true;
  }

  try {
    // Canonical /v1 flow: ensure a conversation, POST the message (returns the assistant message
    // id), then follow the assistant's SSE stream. No legacy single-POST /chat round-trip.
    let cid = conversationId;
    if (!cid) {
      const created = await createConversation({
        title: deriveConversationTitle(message, attachments, opts?.skillTitles),
        projectId: projectId ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
      cid = created.id;
      // Conversation now exists — the dedup window is closed. Release the guard
      // immediately so a different new chat started while this one is still
      // streaming is not falsely blocked.
      resetChatStreamGuard();
    }

    await settlePickSaves(cid, opts?.regenerateOf, signal);
    const skillIds = opts?.skillIds?.length ? { skill_ids: opts.skillIds } : {};
    /**
     * A Retry posts to the stored question: the server writes a new reply to it and no second copy of
     * the question, so the body carries only what a send adds besides the stored text and files. A
     * refused Retry wrote nothing: it says why and lets the caller drop its placeholder. A gateway
     * timeout is not a refusal, since the new answer may be stored, so it ends like a broken connection.
     */
    const postRes = opts?.regenerateOf
      ? await fetch(
          `${API_BASE}/conversations/${cid}/messages/${encodeURIComponent(opts.regenerateOf)}/regenerate`,
          {
            method: 'POST',
            headers: await headers(AGENT_TURN_MIN_VALIDITY_MS),
            body: JSON.stringify({ timezone: browserTz(), ...skillIds }),
            signal,
          },
        )
      : await fetch(`${API_BASE}/conversations/${cid}/messages`, {
          method: 'POST',
          headers: await headers(AGENT_TURN_MIN_VALIDITY_MS),
          body: JSON.stringify({
            content: message,
            ...(opts?.developerReply
              ? {
                  idempotency_key: opts.developerReply.messageId,
                  developer_reply: {
                    message_id: opts.developerReply.messageId,
                    run_id: opts.developerReply.runId,
                    questioned_at: opts.developerReply.questionedAt,
                    action: opts.developerReply.action,
                  },
                }
              : {}),
            attachments,
            /**
             * Browser IANA timezone (shared resolver; falls back to "UTC") so the agent authors
             * schedules in the user's zone. Always present, never omitted.
             */
            timezone: browserTz(),
            ...skillIds,
            ...(opts?.openFile
              ? { open_file: fileReferenceToWire(opts.openFile) }
              : {}),
          }),
          signal,
        });
    if (
      !postRes.ok &&
      opts?.regenerateOf &&
      postRes.status !== 422 &&
      postRes.status !== 504
    ) {
      const body = (await postRes.json().catch(() => null)) as {
        message?: unknown;
      } | null;
      const reason =
        typeof body?.message === 'string'
          ? body.message
          : `HTTP ${postRes.status}`;
      notify.error(reason);
      throw new SendRejectedError(reason, true);
    }
    if (!postRes.ok) {
      if (postRes.status === 409) {
        const body: unknown = await postRes
          .clone()
          .json()
          .catch(() => null);
        if (
          typeof body === 'object' &&
          body !== null &&
          'code' in body &&
          body.code === 'resource_archived'
        ) {
          const reason =
            'message' in body && typeof body.message === 'string'
              ? body.message
              : 'This chat is archived. Restore it first.';
          notify.error('This chat is archived. Restore it first.');
          throw new ResourceArchivedError(reason, true);
        }
      }
      if (opts?.developerReply) {
        const body = (await postRes.json().catch(() => null)) as {
          message?: unknown;
        } | null;
        const error = new SendRejectedError(
          typeof body?.message === 'string'
            ? body.message
            : `Could not send your reply (HTTP ${postRes.status}). Try again.`,
          true,
        );
        if ([403, 404, 409].includes(postRes.status))
          error.name = 'DeveloperReplyUnavailableError';
        throw error;
      }
      /**
       * A 422 here means the server rejected the send synchronously, before
       * creating any message row (e.g. a skill chip that was deleted/disabled
       * after the user picked it). Surface the server's reason as a toast and
       * throw instead of yielding a stream event, so the caller can restore
       * the composer's draft for a retry rather than painting a failed turn.
       */
      if (postRes.status === 422) {
        const body = await postRes
          .json()
          .catch(() => ({ message: `HTTP ${postRes.status}` }));
        const reason =
          typeof body.message === 'string'
            ? body.message
            : `HTTP ${postRes.status}`;
        notify.error(reason);
        throw new SendRejectedError(reason, true);
      }
      /**
       * Any error status means the question was not stored, except a gateway timeout: the service
       * may have stored it before the gateway gave up.
       */
      yield {
        type: 'error',
        data: `HTTP ${postRes.status}`,
        transportError: true,
        ...(postRes.status === 504 ? {} : { sendRefused: true }),
      };
      return;
    }
    const {
      message_id: assistantId,
      routed_to: routedTo,
      user_message_id: userMessageId,
    } = (await postRes.json()) as {
      message_id: string;
      routed_to?: unknown;
      user_message_id?: unknown;
    };
    const route = knownMessageRoute(routedTo);

    // Hand the consumer the ids immediately (new conversations need them to route/persist).
    yield {
      type: 'agent_status',
      data: '',
      conversationId: cid,
      messageId: assistantId,
      ...(route === undefined ? {} : { routedTo: route }),
      ...(typeof userMessageId === 'string' && !opts?.regenerateOf
        ? { userMessageId }
        : {}),
    };

    /** A Developer task works out of the chat, so nothing of it streams into this send (MEDA-1212). */
    if (route === 'developer_task') return;
    yield* followMessageStream(cid, assistantId, signal);
  } finally {
    resetChatStreamGuard();
  }
}

/**
 * Highest SSE `id:` cursor seen per assistant message stream. `streamResume`
 * reads it to send `Last-Event-ID` when it re-follows a stream after a HITL
 * interrupt, so the server skips already-replayed events. Entries are dropped
 * on terminal `done`/`cancelled`/`error` frames; an interrupt keeps its entry
 * alive because the resume flow still needs the cursor. Keyed by
 * `conversationId:messageId` (the endpoints are conversation-scoped) so
 * cursors never collide across conversations.
 */
const messageStreamCursors = new Map<string, number>();

/**
 * Bound on live cursors. An interrupted turn the user never resumes (navigated
 * away / closed the tab) keeps its cursor forever, so cap the map and evict the
 * oldest entry (Map preserves insertion order) when a new key would exceed it.
 */
export const MAX_STREAM_CURSORS = 100;

/** Composite cursor-map key — the stream/resume endpoints are conversation-scoped. */
function cursorKey(conversationId: string, messageId: string): string {
  return `${conversationId}:${messageId}`;
}

/**
 * Record a cursor for `key`, keeping the map bounded. Evicts the oldest entry
 * only when adding a genuinely new key at capacity — updating an existing
 * cursor never evicts and keeps its insertion position.
 */
function setStreamCursor(key: string, eventId: number): void {
  if (
    !messageStreamCursors.has(key) &&
    messageStreamCursors.size >= MAX_STREAM_CURSORS
  ) {
    const oldestKey = messageStreamCursors.keys().next().value;
    if (oldestKey !== undefined) messageStreamCursors.delete(oldestKey);
  }
  messageStreamCursors.set(key, eventId);
}

/** Test-only: current live-cursor count, for asserting the bounded-map cap. */
export function streamCursorCountForTest(): number {
  return messageStreamCursors.size;
}

/** A canonical snapshot carries its full result and its existing terminal semantics. */
function snapshotStreamEvent(
  snapshot: Message,
  conversationId: string,
): StreamEvent {
  const pending = effectivePendingInterrupt(snapshot);
  const type =
    snapshot.status === 'completed'
      ? 'done'
      : snapshot.status === 'failed'
        ? 'error'
        : snapshot.status === 'cancelled'
          ? 'cancelled'
          : pending
            ? 'interrupt'
            : 'message_snapshot';
  return {
    ...pending,
    type,
    conversationId,
    messageId: snapshot.id,
    messageSnapshot: snapshot,
    turnSeq: snapshot.turnSeq,
    ...(type === 'error'
      ? { data: snapshot.errorReason ?? 'Agent execution failed' }
      : {}),
  };
}

/**
 * Consume the conversation-service SSE stream for one assistant message and translate the
 * platform events (`started | token | agent_status | interrupt | done | cancelled | error`)
 * into the client's StreamEvent shape. The platform stream uses `event:`/`data:` blocks with
 * an `id:` cursor for legacy replies. Durable replies use `turn_seq` independently of Redis ids
 * and recover replay gaps from a complete message snapshot. After a decision, skip one replay of
 * that approved interrupt and keep reading.
 */
async function* followMessageStream(
  conversationId: string,
  messageId: string,
  signal?: AbortSignal,
  fromId = 0,
  approvedInterruptId?: string,
  afterTurnSeq?: number,
): AsyncGenerator<StreamEvent> {
  const key = cursorKey(conversationId, messageId);
  const streamHeaders = await headers();
  if (afterTurnSeq === undefined && fromId > 0)
    streamHeaders['Last-Event-ID'] = String(fromId);
  const res = await fetch(
    `${API_BASE}/conversations/${conversationId}/messages/${messageId}/stream`,
    { method: 'GET', headers: streamHeaders, signal },
  );
  if (!res.ok) {
    messageStreamCursors.delete(key);
    yield { type: 'error', data: `HTTP ${res.status}`, transportError: true };
    return;
  }
  /**
   * An OK response can still carry a null body (unsupported streaming, some
   * intermediaries). Guard before getReader() so no throw escapes uncaught and
   * skips cursor cleanup — mirror the `!res.ok` early-exit.
   */
  if (!res.body) {
    messageStreamCursors.delete(key);
    yield { type: 'error', data: 'stream body missing', transportError: true };
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  /**
   * An interrupt closes the stream but keeps the cursor for the resume
   * re-follow, and a `done` that still awaits approval keeps it for the same
   * reason — the row is still `streaming` and the decision still submittable,
   * so a retried resume re-follows this stream and must send `Last-Event-ID`
   * (a fresh follow replays the server's whole retained buffer). Every other
   * exit (ordinary done/cancelled/error, a normal end, or a network drop /
   * abort that throws out of the reader) must drop it so a stale cursor never
   * leaks. The finally does the cleanup unless this flag says the exit left
   * the turn resumable.
   */
  let preserveCursor = false;
  let pendingApprovedInterruptId = approvedInterruptId;
  let latestTurnSeq = afterTurnSeq ?? 0;
  let durable = afterTurnSeq !== undefined;
  /** A snapshot older than applied output cannot replace it. */
  const readSnapshot = async (): Promise<Message | undefined> => {
    const messages = await fetchMessages(conversationId, signal);
    const snapshot = messages.find((message) => message.id === messageId);
    return snapshot?.turnSeq !== undefined && snapshot.turnSeq >= latestTurnSeq
      ? snapshot
      : undefined;
  };
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() || '';
      for (const block of blocks) {
        let event = 'message';
        let data = '';
        for (const line of block.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) data = line.slice(5).trim();
          else if (line.startsWith('id:')) {
            const eventId = Number(line.slice(3).trim());
            if (
              Number.isFinite(eventId) &&
              eventId > (messageStreamCursors.get(key) ?? 0)
            ) {
              setStreamCursor(key, eventId);
            }
          }
        }
        if (event === 'stream-reset' && durable) {
          const snapshot = await readSnapshot();
          if (!snapshot)
            throw new Error('Could not restore the current reply snapshot');
          latestTurnSeq = snapshot.turnSeq!;
          const restored = snapshotStreamEvent(snapshot, conversationId);
          yield restored;
          if (restored.type !== 'message_snapshot') return;
          continue;
        }
        let parsed: Record<string, unknown> = {};
        if (data) {
          try {
            parsed = JSON.parse(data) as Record<string, unknown>;
          } catch {
            if (event !== 'resumed') continue;
          }
        } else if (event !== 'resumed') continue;
        const turnSeq =
          typeof parsed.turn_seq === 'number' &&
          Number.isSafeInteger(parsed.turn_seq) &&
          parsed.turn_seq >= 0
            ? parsed.turn_seq
            : undefined;
        if (turnSeq !== undefined && turnSeq <= latestTurnSeq) continue;
        if (turnSeq !== undefined) {
          durable = true;
          if (turnSeq > latestTurnSeq + 1) {
            const snapshot = await readSnapshot();
            if (!snapshot || snapshot.turnSeq! < turnSeq)
              throw new Error('Could not restore missing reply output');
            latestTurnSeq = snapshot.turnSeq!;
            const restored = snapshotStreamEvent(snapshot, conversationId);
            yield restored;
            if (restored.type !== 'message_snapshot') return;
            continue;
          }
        }
        let emitted = false;
        /** Keep the durable cursor separate from Redis transport ids. */
        const withTurnCursor = (frame: StreamEvent): StreamEvent => {
          emitted = true;
          return turnSeq === undefined ? frame : { ...frame, turnSeq };
        };
        if (event === 'resumed') {
          yield withTurnCursor({ type: 'resumed', conversationId, messageId });
          if (turnSeq !== undefined) latestTurnSeq = turnSeq;
          continue;
        }
        switch (event) {
          case 'token':
            yield withTurnCursor({
              type: 'token',
              data: String(parsed.token ?? ''),
            });
            break;
          case 'agent_status': {
            /**
             * `operations` names the tools a silent step is about to call —
             * the only content such a frame has (no `name`). Forwarded so the
             * UI can render a fallback thinking step; anything non-string in
             * the list is dropped rather than trusted.
             */
            const operations = Array.isArray(parsed.operations)
              ? parsed.operations
                  .filter(
                    (operationName): operationName is string =>
                      typeof operationName === 'string',
                  )
                  .map((operationName) => operationName.trim())
                  .filter((operationName) => operationName.length > 0)
              : [];
            const operationOutcomes = Array.isArray(parsed.operationOutcomes)
              ? parsed.operationOutcomes.filter(
                  (outcome): outcome is { name: string; ok: boolean } =>
                    typeof outcome?.name === 'string' &&
                    typeof outcome?.ok === 'boolean',
                )
              : [];
            yield withTurnCursor({
              type: 'agent_status',
              data: String(parsed.name ?? parsed.status ?? ''),
              ...(operations.length > 0 ? { operations } : {}),
              ...(typeof parsed.agent === 'string'
                ? { agent: parsed.agent }
                : {}),
              ...(typeof parsed.callId === 'string'
                ? { callId: parsed.callId }
                : {}),
              ...(operationOutcomes.length > 0 ? { operationOutcomes } : {}),
            });
            break;
          }
          case 'setup_action':
            yield withTurnCursor(setupActionFields(parsed));
            break;
          case 'heartbeat':
            yield withTurnCursor({
              type: 'heartbeat',
              data: String(parsed.content ?? ''),
            });
            break;
          case 'dashboard': {
            /**
             * Generative-dashboard snapshot (OpenUI Lang + inline data). Validate
             * the envelope; a malformed payload is ignored so the turn degrades to
             * the markdown reply (additive — invariants 4, 6).
             */
            const snapshot = dashboardSnapshotSchema.safeParse(parsed);
            if (snapshot.success) {
              yield withTurnCursor({
                type: 'dashboard',
                dashboardSpec: snapshot.data,
              });
            }
            break;
          }
          case 'workflow': {
            /**
             * A run the agent started mid-turn. The broker data is `{ workflowId, workflowData }`;
             * a frame without a workflow id cannot be bound to anything and is dropped.
             */
            const workflowId =
              typeof parsed.workflowId === 'string'
                ? parsed.workflowId.trim()
                : '';
            if (workflowId) {
              yield withTurnCursor({
                type: 'workflow',
                workflowId,
                workflowData: parsed.workflowData,
              });
            }
            break;
          }
          case 'quality_generation_plan_updated': {
            const receipt = parseGenerationPlanReceipt(parsed);
            if (receipt)
              yield withTurnCursor({
                type: 'quality_generation_plan_updated',
                qualityGenerationReceipt: receipt,
              });
            break;
          }
          case 'diagram': {
            /** A diagram preview; a malformed envelope is dropped and the turn continues as a normal reply. */
            const diagramSpec = parseDiagramSpec(parsed);
            if (diagramSpec)
              yield withTurnCursor({ type: 'diagram', diagramSpec });
            break;
          }
          case 'artifact': {
            /**
             * A file the agent registered in the workspace artifacts registry mid-turn. The frame is
             * snake_case on the wire (`workspace_id`, `conversation_id`, `content_type`) and is mapped
             * to camelCase here, exactly like the REST `fromWire` mapper: an absent content type
             * becomes `''` and an absent size `-1` (unknown). Accepted only with a non-empty `id` and
             * `name` — the id addresses the content route and the name is the only thing the header can
             * show, so a frame without them is unusable. A malformed frame is dropped so the turn
             * degrades to its plain markdown reply (same rule as `dashboard`).
             */
            const artifactId =
              typeof parsed.id === 'string' ? parsed.id.trim() : '';
            const artifactName =
              typeof parsed.name === 'string' ? parsed.name.trim() : '';
            if (!artifactId || !artifactName) break;
            const artifactSize = parsed.size;
            yield withTurnCursor({
              type: 'artifact',
              artifact: {
                id: artifactId,
                workspaceId:
                  typeof parsed.workspace_id === 'string'
                    ? parsed.workspace_id
                    : '',
                conversationId:
                  typeof parsed.conversation_id === 'string'
                    ? parsed.conversation_id
                    : null,
                name: artifactName,
                contentType:
                  typeof parsed.content_type === 'string'
                    ? parsed.content_type
                    : '',
                size:
                  typeof artifactSize === 'number' &&
                  Number.isFinite(artifactSize)
                    ? artifactSize
                    : -1,
              },
            });
            break;
          }
          case 'progress': {
            /**
             * A live progress step: an insights run stage, or a Developer
             * activity line carried in `stageKey`. Only emit a well-formed
             * frame: a non-empty stage key and a finite, non-negative integer
             * order. A malformed frame is ignored (additive: the turn still
             * streams tokens); a missing order would coerce to NaN and break
             * downstream dedupe/ordering.
             */
            const stageKey = String(parsed.stageKey ?? '').trim();
            const order = Number(parsed.order);
            if (stageKey && Number.isInteger(order) && order >= 0) {
              yield withTurnCursor({
                type: 'progress',
                stageKey,
                order,
                ...(typeof parsed.at === 'string' ? { at: parsed.at } : {}),
              });
            }
            break;
          }
          case 'interrupt': {
            const action = parsed.action;
            const hasValidAction =
              typeof action === 'object' &&
              action !== null &&
              typeof (action as { tool?: unknown }).tool === 'string' &&
              (action as { tool: string }).tool.length > 0;
            const allowedDecisions = Array.isArray(parsed.allowedDecisions)
              ? (parsed.allowedDecisions.filter(
                  (decision): decision is ResumeDecisionType =>
                    decision === 'approve' ||
                    decision === 'edit' ||
                    decision === 'reject',
                ) as ResumeDecisionType[])
              : [];
            /**
             * A malformed frame — no interruptId, an action without a string
             * tool, or zero supported decisions (an ApprovalCard with no
             * approve/edit/reject buttons is a dead end) — would leave the user
             * stuck, so drop it and keep reading the stream rather than ending
             * the turn on a bad frame.
             */
            if (
              typeof parsed.interruptId !== 'string' ||
              !parsed.interruptId ||
              !hasValidAction ||
              allowedDecisions.length === 0
            ) {
              break;
            }
            if (parsed.interruptId === pendingApprovedInterruptId) {
              pendingApprovedInterruptId = undefined;
              break;
            }
            const rawArgs = (action as { args?: unknown }).args;
            const args =
              typeof rawArgs === 'object' && rawArgs !== null
                ? (rawArgs as Record<string, unknown>)
                : {};
            preserveCursor = true;
            yield withTurnCursor({
              type: 'interrupt',
              interruptId: parsed.interruptId,
              agent: typeof parsed.agent === 'string' ? parsed.agent : '',
              action: { tool: (action as { tool: string }).tool, args },
              description:
                typeof parsed.description === 'string'
                  ? parsed.description
                  : '',
              allowedDecisions,
              category:
                typeof parsed.category === 'string'
                  ? parsed.category
                  : undefined,
              timestamp:
                typeof parsed.timestamp === 'string'
                  ? parsed.timestamp
                  : undefined,
              conversationId:
                typeof parsed.conversationId === 'string'
                  ? parsed.conversationId
                  : conversationId,
              messageId:
                typeof parsed.messageId === 'string'
                  ? parsed.messageId
                  : messageId,
              grant: parseInterruptGrant(parsed.grant),
            });
            return;
          }
          case 'approval_auto': {
            /**
             * A rule from "Approve & allow similar" approved an operation without a card. The turn
             * keeps going, so this only records the row; a frame without an interrupt id is dropped.
             */
            const autoApproval = parseAutoApproval(parsed);
            if (autoApproval)
              yield withTurnCursor({ type: 'approval_auto', autoApproval });
            break;
          }
          case 'done': {
            /**
             * `awaitingApproval` says the turn ENDED without producing a reply
             * because the approval never reached the agent — the server left
             * the row `streaming` and the decision still submittable. Dropping
             * the flag here (which is what used to happen) turned that into an
             * ordinary finish: the card was already cleared optimistically, so
             * the user was left with no card and no message. The server only
             * ever sends `awaitingApproval: true`, so the flag is forwarded as
             * a presence marker (never an explicit `false`), and the cursor is
             * kept exactly as the interrupt branch keeps it — the retried
             * resume re-follows this stream and would otherwise replay it from
             * the start.
             */
            const stillAwaitingApproval = parsed.awaitingApproval === true;
            if (stillAwaitingApproval) preserveCursor = true;
            /**
             * `failedTools` names the tools that failed in a turn that still completed. The server
             * sends it only when non-empty, so anything but a non-empty array of strings is dropped.
             */
            const failedTools = Array.isArray(parsed.failedTools)
              ? parsed.failedTools.filter(
                  (toolName): toolName is string =>
                    typeof toolName === 'string',
                )
              : [];
            const developerConfirmation = knownDeveloperConfirmation(
              parsed.developerConfirmation,
            );
            yield withTurnCursor({
              type: 'done',
              conversationId,
              messageId,
              ...(stillAwaitingApproval ? { awaitingApproval: true } : {}),
              ...(failedTools.length > 0 ? { failedTools } : {}),
              ...(developerConfirmation === undefined
                ? {}
                : { developerConfirmation }),
            });
            return;
          }
          case 'cancelled':
            /**
             * Terminal server cancel (user stop / supersede / timeout). Yield it
             * so useChat's `cancelled` handler paints the "Stopped" state and its
             * reason — the finally still clears the cursor on return.
             */
            yield withTurnCursor({
              type: 'cancelled',
              reason:
                typeof parsed.reason === 'string'
                  ? (parsed.reason as StreamEvent['reason'])
                  : undefined,
              conversationId,
              messageId,
            });
            return;
          case 'error':
            yield withTurnCursor({
              type: 'error',
              data: String(parsed.error ?? 'agent error'),
            });
            return;
          default:
            break;
        }
        if (emitted && turnSeq !== undefined) latestTurnSeq = turnSeq;
      }
    }
    if (durable && !signal?.aborted) {
      throw new Error('Connection closed before the reply ended');
    }
  } catch (error) {
    if (durable && !signal?.aborted) {
      const snapshot = await readSnapshot().catch(() => undefined);
      if (snapshot) {
        const restored = snapshotStreamEvent(snapshot, conversationId);
        yield restored;
        if (restored.type !== 'message_snapshot') return;
      }
    }
    throw error;
  } finally {
    if (!preserveCursor) messageStreamCursors.delete(key);
    await reader.cancel().catch(() => undefined);
  }
}

/**
 * Rejoin a turn that is still running — the user opened a chat mid-generation, or came back to the
 * one they left. It is the same stream the sender follows, addressed the same way, so reasoning,
 * tool activity and tokens carry on arriving instead of stopping at the moment they navigated.
 *
 * Legacy replies follow `afterSeq`, the highest broker id already reflected on screen. Durable
 * replies instead replay from the retained buffer and filter against `afterTurnSeq`; a Redis reset
 * cannot invalidate that cursor. Missing output and an unconfirmed stream end trigger a canonical
 * snapshot read before more output is applied.
 */
export async function* resumeChat(
  conversationId: string,
  messageId: string,
  afterSeq: number,
  signal?: AbortSignal,
  afterTurnSeq?: number,
): AsyncGenerator<StreamEvent> {
  const key = cursorKey(conversationId, messageId);
  /**
   * `afterSeq` is where this rejoin starts, and it is the caller's to decide. The cursor map is
   * shared and only ever moves forward, so a watermark left by an earlier follow of the same message
   * — an interrupt keeps its cursor on purpose — would outrank every id replayed now and stamp all
   * of them with that number. A retry would then resume past events it never painted.
   */
  messageStreamCursors.delete(key);
  for await (const event of followMessageStream(
    conversationId,
    messageId,
    signal,
    afterSeq,
    undefined,
    afterTurnSeq,
  )) {
    /**
     * Stamp each frame with the broker id it arrived under. The parser tracks those ids for its own
     * `Last-Event-ID` bookkeeping but does not put them on the events, and a caller retrying a rejoin
     * that died mid-stream has nothing else to resume from — it would ask from the opening cursor
     * again and paint the tokens it has already shown a second time.
     */
    const seq = messageStreamCursors.get(key);
    yield typeof seq === 'number' ? { ...event, seq } : event;
  }
}

/**
 * Read an `approval_auto` frame (`{ interruptId, grantId, tool, description, action }`). The tool
 * falls back to `action.tool`. Null without an interrupt id, which the row is keyed on.
 */
function parseAutoApproval(
  parsed: Record<string, unknown>,
): AutoApproval | null {
  const interruptId = parsed.interruptId;
  if (typeof interruptId !== 'string' || !interruptId) return null;
  const action = parsed.action as { tool?: unknown } | null | undefined;
  const tool =
    typeof parsed.tool === 'string'
      ? parsed.tool
      : typeof action?.tool === 'string'
        ? action.tool
        : '';
  return {
    interruptId,
    grantId: typeof parsed.grantId === 'string' ? parsed.grantId : null,
    tool,
    description:
      typeof parsed.description === 'string' ? parsed.description : '',
  };
}

/** Map a camelCase ResumeDecision to its snake_case wire shape (`edited_args`). */
function toWireDecision(decision: ResumeDecision): Record<string, unknown> {
  if (decision.type === 'edit')
    return { type: 'edit', edited_args: decision.editedArgs };
  if (decision.type === 'reject') {
    return decision.feedback !== undefined
      ? { type: 'reject', feedback: decision.feedback }
      : { type: 'reject' };
  }
  return { type: 'approve' };
}

/**
 * Error text for a resume POST answered with 409 Conflict: the interrupt was
 * already decided (another tab, a retry) or the turn reached a terminal state
 * on the server. A raw "HTTP 409" would read like a transient failure and
 * invite a retry that can never succeed, so spell out what happened instead.
 */
const RESUME_CONFLICT_MESSAGE =
  'This approval was already decided or the turn has ended. Reload the conversation to see its current state.';

/**
 * Resume a HITL-interrupted turn: POST the user's decision to
 * `POST /v1/conversations/:cid/messages/:mid/resume` (202 Accepted), then
 * re-follow the same message stream. `Last-Event-ID` is sent from the cursor
 * recorded off the interrupted stream's `id:` lines so the server skips
 * events the client already rendered. A persisted card may have no cursor, so
 * the follow also skips a replay of the interrupt just decided.
 */
export async function* streamResume(
  conversationId: string,
  messageId: string,
  payload: ResumePayload,
  signal?: AbortSignal,
  afterTurnSeq?: number,
): AsyncGenerator<StreamEvent> {
  const res = await fetch(
    `${API_BASE}/conversations/${conversationId}/messages/${messageId}/resume`,
    {
      method: 'POST',
      headers: await headers(AGENT_TURN_MIN_VALIDITY_MS),
      body: JSON.stringify({
        interrupt_id: payload.interruptId,
        decision: toWireDecision(payload.decision),
        ...(payload.rememberSimilar === true &&
        payload.decision.type === 'approve'
          ? { remember_similar: true }
          : {}),
      }),
      signal,
    },
  );
  if (res.status === 403) throw new ResumeForbiddenError();
  if (res.status === 409 && (await isApprovalNotGrantable(res))) {
    throw new ApprovalNotGrantableError();
  }
  if (!res.ok) {
    yield {
      type: 'error',
      data: res.status === 409 ? RESUME_CONFLICT_MESSAGE : `HTTP ${res.status}`,
      transportError: true,
    };
    return;
  }
  yield* followMessageStream(
    conversationId,
    messageId,
    signal,
    messageStreamCursors.get(cursorKey(conversationId, messageId)) ?? 0,
    payload.interruptId,
    afterTurnSeq,
  );
}

/**
 * Send the sender's answer to Developer's question `messageId`: Developer takes the original task, or
 * CT Agent answers the original message. The browser timezone rides along as on a send. A 403 here
 * refuses this answer (not the sender, or Developer unavailable), so it never redirects to access denied.
 *
 * @throws DeveloperConfirmationRejectedError on a 403 or 409 refusal, with the service's reason.
 * @throws ApiError on any other failure, with the service's reason.
 * @returns the id of the new reply the choice started, which the caller follows like any running turn.
 */
/**
 * Asks the service to stop the Developer run behind a watched message (MEDA-1194). True only when the
 * service says the run stopped; false when it could not, or when the request failed, so the caller never
 * reports a stop that did not happen.
 */
export async function stopDeveloperTask(
  conversationId: string,
  messageId: string,
): Promise<boolean> {
  try {
    const res = await fetch(
      `${API_BASE}/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/cancel`,
      { method: 'POST', headers: await headers() },
    );
    if (!res.ok) return false;
    const body = (await res.json().catch(() => null)) as {
      cancelled?: unknown;
    } | null;
    return body?.cancelled === true;
  } catch {
    return false;
  }
}

export async function decideDeveloperConfirmation(
  conversationId: string,
  messageId: string,
  decision: DeveloperConfirmationDecision,
): Promise<string> {
  const res = await fetch(
    `${API_BASE}/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/developer-confirmation`,
    {
      method: 'POST',
      headers: await headers(AGENT_TURN_MIN_VALIDITY_MS),
      body: JSON.stringify({ decision, timezone: browserTz() }),
    },
  );
  if (!res.ok) {
    const failure = await apiErrorFromResponse(
      res,
      'Could not record your choice',
    );
    if (res.status === 403 || res.status === 409) {
      throw new DeveloperConfirmationRejectedError(
        failure.status,
        failure.message,
      );
    }
    throw failure;
  }
  const { message_id: followUpId } = (await res.json()) as {
    message_id: string;
  };
  return followUpId;
}
